// ══════════════════════════════════════════════════════════════════════════════
// JourneyPage.js — 21-Day Transformation Journey
// Flow: Select Journey → Emotion Chip → Verse → Slider → Save
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useRef, useCallback } from "react";
import { generateJourneyCard, generateDayProgressCard, downloadCard } from "./ShlokaCard";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

// ── Sankalpa Challenge data (mirrors journey_data.py) ────────────────────────

const DAY_NAMES = [
  { day:  1, name: "The Sankalpa",     theme: "Set your vow — what are you releasing?" },
  { day:  2, name: "The Witness",      theme: "Observe yourself without judgment" },
  { day:  3, name: "The Breath",       theme: "Pranayama as your anchor today" },
  { day:  4, name: "The Body",         theme: "Where does this struggle live in you?" },
  { day:  5, name: "The Invitation",   theme: "Today's practice is service — invite 3 people" },
  { day:  6, name: "The Story",        theme: "The narrative you have been carrying" },
  { day:  7, name: "The Mirror",       theme: "Week 1 complete — what have you seen?" },
  { day:  8, name: "The Turn",         theme: "What if you are wrong about this?" },
  { day:  9, name: "The Root",         theme: "Where did this pattern first begin?" },
  { day: 10, name: "The Mantra",       theme: "Today the mantra IS the practice — 108 times" },
  { day: 11, name: "The Surrender",    theme: "Release the outcome you are gripping" },
  { day: 12, name: "The Relationship", theme: "How does this struggle touch those around you?" },
  { day: 13, name: "The Teacher",      theme: "What is this struggle teaching you?" },
  { day: 14, name: "The Halfway",      theme: "Something has already shifted — name it" },
  { day: 15, name: "The Shift",        theme: "Name what is different in you now" },
  { day: 16, name: "The Practice",     theme: "How will you carry this into daily life?" },
  { day: 17, name: "The Gratitude",    theme: "Give thanks — even for the struggle" },
  { day: 18, name: "The Gift",         theme: "What will you give others from what you've learned?" },
  { day: 19, name: "The Vow",          theme: "Renew your sankalpa — this time for life" },
  { day: 20, name: "The Prayer",       theme: "Place it all at the feet of the divine" },
  { day: 21, name: "The New Beginning",theme: "Your 21 days are complete — you are not the same" },
];

const WEEK_MANTRAS = {
  1: {
    text: "Om Namah Shivaya",
    devanagari: "ॐ नमः शिवाय",
    count: "5×",
    meaning: "I bow to the auspicious one — the divine within",
    instruction: "Close your eyes. Take one slow breath. Chant aloud 5 times before today's verse.",
  },
  2: {
    text: "Hare Krishna Maha Mantra",
    devanagari: "हरे कृष्ण हरे कृष्ण कृष्ण कृष्ण हरे हरे\nहरे राम हरे राम राम राम हरे हरे",
    count: "7×",
    meaning: "The Maha Mantra — a direct call to the divine",
    instruction: "The Maha Mantra needs no translation. Let it move through you 7 times.",
  },
  3: {
    text: "Om Shanti Shanti Shanti",
    devanagari: "ॐ शान्ति शान्ति शान्ति",
    count: "21×",
    meaning: "Peace in body. Peace in mind. Peace in spirit.",
    instruction: "21 repetitions — one for each day of your sankalpa. Let each seal your transformation.",
  },
};

const PRANAYAMA_STEPS = [
  "Sit upright. Close your right nostril with your right thumb.",
  "Inhale slowly through your left nostril — 4 counts.",
  "Close both nostrils. Hold — 4 counts.",
  "Release right thumb. Exhale through right nostril — 8 counts.",
  "Inhale through right — 4. Hold — 4. Exhale left — 8.",
  "That is one round. Do 5 rounds total.",
];

const JOURNALING_PROMPTS = {
  8:  "What is one belief about this struggle that you have been carrying as truth — but might not be?",
  9:  "When did you first feel this way? What age were you? What was happening around you then?",
  10: "Write 108 words — stream of consciousness — starting with: 'The truth about this struggle is...'",
  11: "What result have you been desperately trying to control? Write it. Then write below it: 'I release you.'",
  12: "How has this struggle affected someone you love — even if they have no idea it has?",
  13: "If this struggle were a wise teacher, what lesson has it been trying to give you that you keep refusing?",
  14: "You are halfway. What have you let go of that you did not expect to? What has quietly, unexpectedly shifted?",
};

const MILESTONE_DAYS = {
  7:  { label: "Week 1 Complete",    sub: "The Mirror — you have been seeing clearly.", color: "#7C3AED", bg: "rgba(124,58,237,0.08)", border: "rgba(124,58,237,0.3)" },
  14: { label: "Halfway — Day 14",   sub: "The Halfway — something has already shifted in you.", color: "var(--gold)", bg: "rgba(212,175,55,0.08)", border: "rgba(212,175,55,0.3)" },
  21: { label: "Sankalpa Complete",  sub: "The New Beginning — you are not who you were on Day 1.", color: "#E8832A", bg: "rgba(232,131,42,0.1)", border: "rgba(232,131,42,0.4)" },
};

// Identity label by journey type — used in verse intro and completion view
const JOURNEY_IDENTITY = {
  career_stress:    "Karma Yogi",
  relationship_pain:"Loving Heart",
  fear_anxiety:     "Fearless Sadhak",
  grief_loss:       "Healing Soul",
  anger:            "Still Waters Sadhak",
  finding_purpose:  "Dharma Seeker",
};

// ── Tiny reusable components ──────────────────────────────────────────────────

function Card({ children, style = {} }) {
  return (
    <div style={{
      background: "var(--bg2)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius)",
      padding: window.innerWidth < 600 ? "16px" : "28px",
      marginBottom: "20px",
      boxShadow: "var(--card-shadow)",
      ...style,
    }}>
      {children}
    </div>
  );
}

function Label({ children, style = {} }) {
  return (
    <div style={{
      fontSize: "1.05rem", color: "var(--saffron)",
      fontWeight: "600", marginBottom: "12px", ...style,
    }}>
      {children}
    </div>
  );
}

function Btn({ children, onClick, disabled, outline, style = {} }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: "10px 24px", borderRadius: "8px", cursor: disabled ? "default" : "pointer",
      fontFamily: "'Inter',sans-serif", fontSize: "1rem", fontWeight: "600", transition: "all 0.2s",
      border: outline ? "1px solid var(--saffron)" : "1px solid var(--saffron)",
      background: outline ? "var(--bg2)" : "var(--saffron)",
      color: disabled ? "var(--text-dim)" : (outline ? "var(--saffron)" : "white"),
      opacity: disabled ? 0.5 : 1,
      ...style,
    }}>
      {children}
    </button>
  );
}

// ── Day Name Banner ───────────────────────────────────────────────────────────

function DayNameBanner({ day, journeyTitle }) {
  const info = DAY_NAMES[Math.min(day - 1, 20)];
  const week = day <= 7 ? 1 : day <= 14 ? 2 : 3;
  const weekColors = ["", "#E8832A", "#7C3AED", "#4caf6f"];
  const weekLabels = ["", "Week 1 · Purification", "Week 2 · Deepening", "Week 3 · Integration"];
  const color = weekColors[week];

  return (
    <div style={{
      marginBottom: "20px",
      padding: "20px 24px",
      background: "var(--bg2)",
      border: "1px solid var(--border)",
      borderLeft: `4px solid ${color}`,
      borderRadius: "12px",
      boxShadow: "var(--card-shadow)",
    }}>
      <div style={{ fontSize: "0.95rem", color, fontWeight: "600", marginBottom: "6px" }}>
        Day {day} of 21 · {weekLabels[week]}
      </div>
      <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.5rem", color: "var(--text)", marginBottom: "4px" }}>
        {info.name}
      </div>
      <div style={{ fontSize: "1rem", color: "var(--text-dim)" }}>
        {info.theme}
      </div>
    </div>
  );
}

// ── Mantra Chip ───────────────────────────────────────────────────────────────

function MantraChip({ week }) {
  const [open, setOpen] = useState(false);
  const mantra = WEEK_MANTRAS[week] || WEEK_MANTRAS[1];

  return (
    <div style={{ marginBottom: "16px" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", gap: "10px",
          padding: "10px 16px", borderRadius: "24px", cursor: "pointer",
          background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.35)",
          color: "var(--gold)", fontFamily: "'Inter',sans-serif", fontSize: "1rem",
          transition: "all 0.2s", width: "100%", textAlign: "left",
        }}
      >
        <span style={{ fontSize: "1.1rem" }}>🔔</span>
        <span style={{ flex: 1 }}>This week's mantra: <strong>{mantra.text.split("\n")[0]}</strong> · {mantra.count}</span>
        <span style={{ fontSize: "0.95rem", opacity: 0.7 }}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div style={{
          marginTop: "8px", padding: "20px 22px",
          background: "rgba(212,175,55,0.05)", border: "1px solid rgba(212,175,55,0.25)",
          borderRadius: "12px",
        }}>
          <div style={{
            fontFamily: "'Noto Sans Devanagari',serif", fontSize: "1.4rem",
            color: "var(--gold)", lineHeight: "1.8", marginBottom: "10px",
            textAlign: "center",
          }}>
            {mantra.devanagari}
          </div>
          <div style={{ textAlign: "center", fontSize: "1rem", color: "var(--text-dim)", marginBottom: "16px" }}>
            {mantra.meaning}
          </div>
          <div style={{
            padding: "14px 16px", background: "rgba(232,131,42,0.06)",
            border: "1px solid rgba(232,131,42,0.2)", borderRadius: "8px",
            fontSize: "1rem", color: "var(--text)", lineHeight: "1.7",
          }}>
            {mantra.instruction}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Practice Card ─────────────────────────────────────────────────────────────

function PracticeCard({ day, practiceType, journalingPrompt, aiAction, actionDone, onActionDone, onContinue }) {
  const week = day <= 7 ? 1 : day <= 14 ? 2 : 3;

  if (practiceType === "pranayama" || week === 1) {
    return (
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
          <div style={{
            width: "36px", height: "36px", borderRadius: "50%", flexShrink: 0,
            background: "linear-gradient(135deg,#E8832A,var(--gold))",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem",
          }}>🌬️</div>
          <div>
            <Label style={{ marginBottom: "0" }}>Today's Practice — Pranayama</Label>
            <div style={{ fontSize: "0.9rem", color: "var(--text-dim)" }}>
              Alternate nostril breathing · 5 rounds · ~3 minutes
            </div>
          </div>
        </div>

        <div style={{ marginBottom: "20px" }}>
          {PRANAYAMA_STEPS.map((step, i) => (
            <div key={i} style={{
              display: "flex", gap: "12px", alignItems: "flex-start",
              marginBottom: "10px",
            }}>
              <div style={{
                width: "24px", height: "24px", borderRadius: "50%", flexShrink: 0,
                background: "rgba(232,131,42,0.15)", border: "1px solid rgba(232,131,42,0.4)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "0.95rem", color: "#E8832A", fontWeight: "700",
              }}>{i + 1}</div>
              <div style={{ fontSize: "1rem", color: "var(--text)", lineHeight: "1.6", paddingTop: "2px" }}>
                {step}
              </div>
            </div>
          ))}
        </div>

        <div style={{
          padding: "12px 16px", background: "rgba(76,175,111,0.06)",
          border: "1px solid rgba(76,175,111,0.2)", borderRadius: "8px",
          fontSize: "0.95rem", color: "#3d8a5e", marginBottom: "20px",
        }}>
          This balances the nervous system before the verse enters you. Do not skip it.
        </div>

        <div onClick={() => onActionDone(!actionDone)} style={{
          display: "flex", alignItems: "center", gap: "12px",
          marginBottom: "24px", cursor: "pointer", userSelect: "none",
        }}>
          <div style={{
            width: "22px", height: "22px", borderRadius: "5px", flexShrink: 0,
            border: actionDone ? "none" : "1px solid var(--border)",
            background: actionDone ? "linear-gradient(135deg,var(--saffron),var(--gold))" : "var(--bg3)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "0.95rem", color: "white", transition: "all 0.2s",
          }}>
            {actionDone ? "✓" : ""}
          </div>
          <span style={{ fontSize: "1rem", color: actionDone ? "var(--cream)" : "var(--text-dim)" }}>
            {actionDone ? "Done — I breathed before reading" : "I'll do this before today's verse"}
          </span>
        </div>
        <Btn onClick={onContinue}>{actionDone ? "Now — how do I feel? →" : "Continue →"}</Btn>
      </Card>
    );
  }

  if (practiceType === "journaling" || week === 2) {
    const prompt = journalingPrompt || JOURNALING_PROMPTS[day] || "Write freely about what today's verse brings up in you.";
    return (
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
          <div style={{
            width: "36px", height: "36px", borderRadius: "50%", flexShrink: 0,
            background: "linear-gradient(135deg,#7C3AED,#6366F1)",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem",
          }}>✍️</div>
          <div>
            <Label style={{ marginBottom: "0" }}>Today's Practice — Journaling</Label>
            <div style={{ fontSize: "0.9rem", color: "var(--text-dim)" }}>
              One question. Write without stopping for 5 minutes.
            </div>
          </div>
        </div>

        <div style={{
          padding: "20px 22px",
          background: "rgba(124,58,237,0.04)", border: "1px solid rgba(124,58,237,0.2)",
          borderRadius: "10px", marginBottom: "20px",
        }}>
          <div style={{ fontSize: "0.95rem", color: "#7C3AED", marginBottom: "8px" }}>
            Today's Prompt
          </div>
          <div style={{ fontSize: "1.15rem", color: "var(--text)", lineHeight: "1.7" }}>
            "{prompt}"
          </div>
        </div>

        <div style={{
          padding: "12px 16px", background: "rgba(232,131,42,0.05)",
          border: "1px solid rgba(232,131,42,0.15)", borderRadius: "8px",
          fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "20px",
        }}>
          Write in any notebook, phone notes, or just in your mind. Do not edit. Do not stop. Let it come.
        </div>

        <div onClick={() => onActionDone(!actionDone)} style={{
          display: "flex", alignItems: "center", gap: "12px",
          marginBottom: "24px", cursor: "pointer", userSelect: "none",
        }}>
          <div style={{
            width: "22px", height: "22px", borderRadius: "5px", flexShrink: 0,
            border: actionDone ? "none" : "1px solid var(--border)",
            background: actionDone ? "linear-gradient(135deg,#7C3AED,#6366F1)" : "var(--bg3)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "0.95rem", color: "white", transition: "all 0.2s",
          }}>
            {actionDone ? "✓" : ""}
          </div>
          <span style={{ fontSize: "1rem", color: actionDone ? "var(--cream)" : "var(--text-dim)" }}>
            {actionDone ? "Written — I sat with the question" : "I'll write this today"}
          </span>
        </div>
        <Btn onClick={onContinue}>{actionDone ? "Now — how do I feel? →" : "Continue →"}</Btn>
      </Card>
    );
  }

  // Week 3 — Sadhana (AI action, existing flow)
  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
        <div style={{
          width: "36px", height: "36px", borderRadius: "50%", flexShrink: 0,
          background: "linear-gradient(135deg,#4caf6f,#388e5c)",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem",
        }}>🙏</div>
        <div>
          <Label style={{ marginBottom: "0" }}>Today's Sadhana</Label>
          <div style={{ fontSize: "0.9rem", color: "var(--text-dim)" }}>
            One concrete act of practice. Do it today.
          </div>
        </div>
      </div>

      {aiAction ? (
        <div style={{
          fontSize: "1.15rem", lineHeight: "1.9", color: "var(--cream)",
          padding: "20px 24px", background: "rgba(76,175,111,0.07)",
          border: "1px solid rgba(76,175,111,0.2)", borderRadius: "10px", marginBottom: "24px",
        }}>
          {aiAction}
        </div>
      ) : (
        <div style={{ color: "var(--text-dim)", marginBottom: "24px" }}>
          Loading today's practice...
        </div>
      )}

      <div onClick={() => onActionDone(!actionDone)} style={{
        display: "flex", alignItems: "center", gap: "12px",
        marginBottom: "24px", cursor: "pointer", userSelect: "none",
      }}>
        <div style={{
          width: "22px", height: "22px", borderRadius: "5px", flexShrink: 0,
          border: actionDone ? "none" : "1px solid var(--border)",
          background: actionDone ? "linear-gradient(135deg,#4caf6f,#388e5c)" : "var(--bg3)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "0.95rem", color: "white", transition: "all 0.2s",
        }}>
          {actionDone ? "✓" : ""}
        </div>
        <span style={{ fontSize: "1rem", color: actionDone ? "var(--cream)" : "var(--text-dim)" }}>
          {actionDone ? "Done — practice complete" : "I'll do this today"}
        </span>
      </div>
      <Btn onClick={onContinue}>{actionDone ? "Now — how do I feel? →" : "Continue →"}</Btn>
    </Card>
  );
}

// ── Day 5 Invitation Card ─────────────────────────────────────────────────────

function InvitationCard({ journeyTitle, journeyType }) {
  const shareText =
    `🙏 I am on Day 5 of my 21-Day ${journeyTitle} Sankalpa on VedSanga.\n\n` +
    `The Bhagavad Gita — one verse a day, one practice a day, 21 days.\n\n` +
    `If you are carrying something heavy right now, come walk with me.\n\n` +
    `vedsanga.com  •  वेदसङ्ग`;

  const share = () => {
    if (navigator.share) {
      navigator.share({ text: shareText }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
    }
  };

  return (
    <div style={{
      marginTop: "20px",
      padding: "24px",
      background: "var(--bg2)",
      border: "1px solid var(--border)",
      borderRadius: "14px",
      textAlign: "center",
      boxShadow: "var(--card-shadow)",
    }}>
      <div style={{ fontSize: "2rem", marginBottom: "10px" }}>🪷</div>
      <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.1rem", color: "var(--gold)", marginBottom: "8px" }}>
        Day 5: The Invitation
      </div>
      <div style={{ fontSize: "1.05rem", color: "var(--text)", lineHeight: "1.7", marginBottom: "6px" }}>
        Your practice today is <strong>service — seva</strong>.
      </div>
      <div style={{ fontSize: "1rem", color: "var(--text-dim)", lineHeight: "1.7", marginBottom: "20px" }}>
        Share this sankalpa with 3 people who are carrying something heavy right now.
        Your sincerity will call them. This is how a challenge becomes a movement.
      </div>
      <button onClick={share} style={{
        display: "inline-flex", alignItems: "center", gap: "8px",
        padding: "12px 24px", borderRadius: "10px", cursor: "pointer",
        background: "rgba(37,211,102,0.12)", border: "1px solid rgba(37,211,102,0.4)",
        color: "#1a8a3c", fontFamily: "'Inter',sans-serif", fontSize: "1rem", fontWeight: "600",
      }}>
        📱 Share on WhatsApp
      </button>
    </div>
  );
}

// ── Milestone Banner ──────────────────────────────────────────────────────────

function MilestoneBanner({ day }) {
  const m = MILESTONE_DAYS[day];
  if (!m) return null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "14px",
      padding: "16px 20px", marginBottom: "20px",
      background: m.bg, border: `1px solid ${m.border}`,
      borderRadius: "12px",
    }}>
      <div style={{ fontSize: "1.6rem", flexShrink: 0 }}>
        {day === 7 ? "🌙" : day === 14 ? "⚡" : "🏆"}
      </div>
      <div>
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1rem", color: m.color, marginBottom: "2px" }}>
          {m.label}
        </div>
        <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
          {m.sub}
        </div>
      </div>
    </div>
  );
}

// ── Earned Practice Card (Day 21 completion) ──────────────────────────────────

function EarnedPracticeCard({ journeyType }) {
  const [practice, setPractice] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!journeyType) return;
    fetch(`${API}/journey/earned-practice/${journeyType}`)
      .then(r => r.json())
      .then(d => { if (!d.error) setPractice(d); })
      .catch(() => {});
  }, [journeyType]);

  if (!practice) return null;

  return (
    <div style={{
      margin: "8px 0 20px",
      padding: "24px",
      background: "var(--bg2)",
      border: "1px solid var(--border)",
      borderRadius: "16px",
      position: "relative",
      overflow: "hidden",
      boxShadow: "var(--card-shadow)",
    }}>
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "3px",
        background: "linear-gradient(90deg, var(--saffron), var(--gold), var(--gold))",
      }} />
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
        <span style={{ fontSize: "1.4rem" }}>🔑</span>
        <div style={{ fontSize: "0.95rem", color: "var(--gold)", fontWeight: "600" }}>
          Your Earned Practice
        </div>
      </div>
      <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.1rem", color: "var(--text)", marginBottom: "12px" }}>
        {practice.title}
      </div>
      <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "16px" }}>
        Given only to those who complete all 21 days.
      </div>

      {!open ? (
        <button onClick={() => setOpen(true)} style={{
          padding: "10px 20px", borderRadius: "8px", cursor: "pointer",
          background: "var(--bg2)",
          border: "1px solid var(--saffron)", color: "var(--saffron)",
          fontFamily: "'Inter',sans-serif", fontSize: "1rem",
        }}>
          Reveal my practice →
        </button>
      ) : (
        <div>
          <div style={{
            padding: "18px 20px", background: "var(--bg3)",
            border: "1px solid var(--border)", borderRadius: "10px",
            fontSize: "1.05rem", lineHeight: "1.85", color: "var(--text)",
            whiteSpace: "pre-line", marginBottom: "12px",
          }}>
            {practice.text}
          </div>
          <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
            {practice.dedication}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Day Share Card ────────────────────────────────────────────────────────────

function DayShareCard({ day, dayName, dayTheme, journeyTitle, journeyIcon, journeyType, daysChecked }) {
  const [imgUrl, setImgUrl]     = useState(null);
  const [copied, setCopied]     = useState(false);
  const [generating, setGen]    = useState(false);

  const caption =
    `I'm on Day ${day} — "${dayName}" of the 21-Day Sankalpa Challenge.\n` +
    `${dayTheme}\n\n` +
    `Studying the Bhagavad Gita for 21 days to transform ${journeyTitle?.toLowerCase() || "my life"}.\n\n` +
    `Join me: vedsanga.com\n` +
    `#21DayChallenge #Sankalpa #BhagavadGita #VedSanga`;

  const generate = async () => {
    setGen(true);
    try {
      const result = await generateDayProgressCard({
        day,
        dayName,
        dayTheme,
        journeyTitle,
        journeyIcon,
        daysChecked: daysChecked || Array.from({ length: day - 1 }, (_, i) => i + 1),
      });
      setImgUrl(result.url);
    } catch (e) {
      console.error("Card generation failed", e);
    }
    setGen(false);
  };

  const copyCaption = () => {
    navigator.clipboard.writeText(caption).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(caption)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const shareInstagram = () => {
    if (imgUrl) downloadCard(imgUrl, `sankalpa-day-${day}.png`);
    setTimeout(copyCaption, 300);
  };

  return (
    <div style={{
      padding: "20px",
      background: "var(--bg2)",
      border: "1px solid var(--border)",
      borderRadius: "14px",
      marginBottom: "20px",
      boxShadow: "var(--card-shadow)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
        <span style={{ fontSize: "1.2rem" }}>📸</span>
        <div style={{ fontSize: "0.95rem", color: "var(--gold)", fontWeight: "600" }}>
          Share Your Progress
        </div>
      </div>

      {!imgUrl ? (
        <button
          onClick={generate}
          disabled={generating}
          style={{
            width: "100%", padding: "12px", borderRadius: "10px", cursor: generating ? "default" : "pointer",
            background: "var(--bg2)", border: "1px solid var(--saffron)",
            color: "var(--saffron)", fontFamily: "'Inter',sans-serif", fontSize: "1rem",
            opacity: generating ? 0.6 : 1,
          }}
        >
          {generating ? "Generating your card..." : "Generate Day " + day + " card →"}
        </button>
      ) : (
        <div>
          <img
            src={imgUrl}
            alt={`Day ${day} progress card`}
            style={{ width: "100%", borderRadius: "10px", marginBottom: "14px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
          />
          <div style={{
            fontSize: "0.95rem", color: "var(--text-dim)", lineHeight: "1.6",
            padding: "12px 14px", background: "var(--bg3)",
            border: "1px solid var(--border)", borderRadius: "8px", marginBottom: "14px",
            whiteSpace: "pre-line",
          }}>
            {caption}
          </div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button onClick={copyCaption} style={{
              flex: 1, padding: "10px 14px", borderRadius: "8px", cursor: "pointer",
              background: copied ? "rgba(76,175,111,0.15)" : "var(--bg2)",
              border: `1px solid ${copied ? "rgba(76,175,111,0.5)" : "var(--border)"}`,
              color: copied ? "#15803d" : "var(--text-dim)",
              fontFamily: "'Inter',sans-serif", fontSize: "0.95rem", minWidth: "120px",
            }}>
              {copied ? "✓ Copied!" : "Copy Caption"}
            </button>
            <button onClick={shareWhatsApp} style={{
              flex: 1, padding: "10px 14px", borderRadius: "8px", cursor: "pointer",
              background: "rgba(37,211,102,0.1)", border: "1px solid rgba(37,211,102,0.4)",
              color: "#25D366", fontFamily: "'Inter',sans-serif", fontSize: "0.95rem", minWidth: "120px",
            }}>
              WhatsApp
            </button>
            <button onClick={shareInstagram} style={{
              flex: 1, padding: "10px 14px", borderRadius: "8px", cursor: "pointer",
              background: "rgba(232,131,42,0.08)", border: "1px solid rgba(232,131,42,0.35)",
              color: "var(--saffron)", fontFamily: "'Inter',sans-serif", fontSize: "0.95rem", minWidth: "120px",
            }}>
              Download
            </button>
          </div>
          <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", textAlign: "center", marginTop: "10px" }}>
            Post on Instagram &amp; write "Comment your sankalpa below 🙏"
          </div>
        </div>
      )}
    </div>
  );
}

// ── Pod Section (10-person accountability pod) ────────────────────────────────

// ── Satsang Community (100-150 real members, spiritual palette) ───────────────

// Warm spiritual avatar colours — no harsh blues, reds, or purples
const _AVATAR_COLORS = [
  { bg: "rgba(249,115,22,0.18)", border: "rgba(249,115,22,0.45)", text: "#9a3412" },  // saffron
  { bg: "rgba(201,149,58,0.18)", border: "rgba(201,149,58,0.45)", text: "#78350f" },  // gold
  { bg: "rgba(217,119,6,0.18)",  border: "rgba(217,119,6,0.45)",  text: "#92400e" },  // turmeric
  { bg: "rgba(219,112,147,0.15)",border: "rgba(219,112,147,0.35)",text: "#9d174d" },  // lotus
  { bg: "rgba(245,158,11,0.18)", border: "rgba(245,158,11,0.45)", text: "#854d0e" },  // marigold
  { bg: "rgba(180,100,60,0.15)", border: "rgba(180,100,60,0.35)", text: "#7c3f26" },  // kumkum
];

function SatsangAvatar({ name = "", photo = "", size = 44 }) {
  const initials = (name || "S").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
  const c = _AVATAR_COLORS[(name.charCodeAt(0) || 0) % _AVATAR_COLORS.length];
  if (photo) return (
    <img src={photo} alt={name} style={{
      width: size, height: size, borderRadius: "50%", objectFit: "cover",
      border: `2px solid ${c.border}`, flexShrink: 0,
    }} />
  );
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: c.bg, border: `2px solid ${c.border}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Inter',sans-serif",
      fontSize: size * 0.32, color: c.text,
    }}>{initials}</div>
  );
}

// ── Satsang Circle row (family/friends on a journey) ─────────────────────────

function CircleJourneyRow({ member }) {
  const c = _AVATAR_COLORS[(member.name.charCodeAt(0) || 0) % _AVATAR_COLORS.length];
  const firstName = member.name.split(" ")[0];
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "10px",
      padding: "10px 14px", borderRadius: "10px",
      background: member.checked_in_today ? "rgba(232,131,42,0.07)" : "transparent",
      border: `1px solid ${member.checked_in_today ? "rgba(232,131,42,0.2)" : "var(--border)"}`,
    }}>
      <SatsangAvatar name={member.name} photo={member.photo_url} size={38} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "0.95rem", color: "var(--text)", fontFamily: "'Inter',sans-serif" }}>
          {firstName}
        </div>
        {member.on_journey && (
          <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
            Day {member.current_day} · {member.journey_title}
          </div>
        )}
        {!member.on_journey && (
          <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
            Not on a journey yet
          </div>
        )}
      </div>
      <div style={{
        fontSize: "1.1rem", flexShrink: 0,
        opacity: member.on_journey ? 1 : 0.3,
      }}>
        {member.checked_in_today ? "✓" : "○"}
      </div>
    </div>
  );
}

function SatsangCommunity({ user, journeyType, currentDay }) {
  const [pod,            setPod]            = useState(null);
  const [posts,          setPosts]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [postText,       setPostText]       = useState("");
  const [posting,        setPosting]        = useState(false);
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [circleData,     setCircleData]     = useState(null);

  const displayName = user?.name || user?.user_metadata?.full_name
                    || user?.email?.split("@")[0] || "Seeker";

  // Fetch Satsang Circle members on journeys
  useEffect(() => {
    if (!user?.id) return;
    fetch(`${API}/journey/circle-members/${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => { if (!d.error) setCircleData(d); })
      .catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !journeyType) return;
    const avatarUrl = localStorage.getItem("vs_avatar") || user?.user_metadata?.avatar_url || "";
    fetch(`${API}/challenge/join`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user.id, journey_type: journeyType,
        display_name: displayName, photo_url: avatarUrl }),
    })
      .then(r => r.json())
      .then(d => { if (!d.error) setPod(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.id, journeyType]);

  useEffect(() => {
    if (!pod?.pod_id) return;
    fetch(`${API}/challenge/pod/posts/${pod.pod_id}?day=${currentDay}&user_id=${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => setPosts(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [pod?.pod_id, currentDay, user?.id]);

  const toggleReact = (postId) => {
    setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, reactions: p.i_reacted ? p.reactions-1 : p.reactions+1, i_reacted: !p.i_reacted } : p
    ));
    fetch(`${API}/challenge/pod/react`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ post_id: postId, user_id: user.id }),
    }).catch(() => {});
  };

  const submitPost = async () => {
    if (!postText.trim() || posting || !pod?.pod_id) return;
    setPosting(true);
    try {
      const r = await fetch(`${API}/challenge/pod/post`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pod_id: pod.pod_id, user_id: user.id,
          display_name: displayName, day: currentDay, post_text: postText.trim() }),
      });
      const d = await r.json();
      if (d.id) {
        const avatar = localStorage.getItem("vs_avatar") || user?.user_metadata?.avatar_url || "";
        setPosts(prev => [{ id: d.id, user_id: user.id, display_name: displayName,
          day: currentDay, text: postText.trim(), reactions: 0, i_reacted: false, photo_url: avatar }, ...prev]);
        setPostText("");
      }
    } catch {}
    setPosting(false);
  };

  if (loading) return (
    <div style={{ height: 180, borderRadius: 16, background: "var(--bg2)",
      animation: "shimmer 1.4s infinite" }} />
  );
  if (!pod) return null;

  const members      = pod.members || [];
  const shaktiPct    = pod.shakti_pct || 0;
  const shaktiLabel  = pod.shakti_label || "Awakening 🌱";
  const checkedToday = pod.checked_in_today || 0;
  const visible      = showAllMembers ? members : members.slice(0, 30);
  const topPost      = [...posts].filter(p => p.reactions > 0).sort((a, b) => b.reactions - a.reactions)[0];

  const circleMembers = circleData?.members || [];
  const circleOnJourney = circleMembers.filter(m => m.on_journey);
  const circleName = circleData?.circles?.[0]?.name || "";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* ── Satsang Circle (family/friends) ── */}
      {circleMembers.length > 0 && (
        <Card>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div>
              <Label style={{ marginBottom: 2 }}>Your Satsang Circle</Label>
              <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
                {circleName} · {circleOnJourney.length} on a Sankalpa journey
              </div>
            </div>
            <span style={{ fontSize: "1.4rem" }}>🪷</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {circleMembers.map(m => <CircleJourneyRow key={m.user_id} member={m} />)}
          </div>
          {circleOnJourney.length === 0 && (
            <div style={{
              marginTop: 12, padding: "12px 16px", borderRadius: 10,
              background: "rgba(232,131,42,0.05)", border: "1px dashed rgba(232,131,42,0.25)",
              fontSize: "0.9rem", color: "var(--text-dim)", textAlign: "center", }}>
              None of your circle members have started a Sankalpa yet.
              Share vedsanga.com with them 🙏
            </div>
          )}
        </Card>
      )}

      {circleMembers.length === 0 && (
        <div style={{
          padding: "14px 18px", borderRadius: 12,
          background: "rgba(201,149,58,0.06)", border: "1px dashed rgba(201,149,58,0.25)",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{ fontSize: "1.3rem", flexShrink: 0 }}>🪷</span>
          <div>
            <div style={{ fontSize: "0.95rem", color: "var(--gold)", fontFamily: "'Inter',sans-serif", marginBottom: 2 }}>
              Walk with your family
            </div>
            <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
              Create a Satsang Circle and invite family to join your 21-day journey together.
              Go to the <strong style={{ color: "var(--saffron)" }}>Satsang</strong> tab to create one.
            </div>
          </div>
        </div>
      )}

      {/* ── Satsang Header + Shakti ── */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.05rem",
              color: "var(--gold)", marginBottom: 4 }}>
              🕉️ {pod.pod_name}
            </div>
            <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
              {members.length} sadhaks walking the same path
            </div>
          </div>
          <div style={{
            background: "rgba(201,149,58,0.1)", border: "1px solid rgba(201,149,58,0.3)",
            borderRadius: 20, padding: "4px 12px",
            fontSize: "0.95rem", color: "var(--gold)",
            fontFamily: "'Inter',sans-serif",
          }}>Day {currentDay}</div>
        </div>

        <div style={{ marginBottom: 4 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
              Collective Shakti — <span style={{ color: "var(--saffron)" }}>{shaktiLabel}</span>
            </span>
            <span style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
              {checkedToday} / {members.length} today
            </span>
          </div>
          <div style={{ height: 7, borderRadius: 4, background: "var(--bg3)", overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 4, width: `${shaktiPct}%`,
              background: "linear-gradient(90deg,var(--saffron),var(--gold))",
              transition: "width 1.2s ease",
              boxShadow: shaktiPct > 40 ? "0 0 10px rgba(249,115,22,0.5)" : "none",
            }} />
          </div>
        </div>
      </Card>

      {/* ── Member Grid ── */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <Label>Sadhaks in Your Satsang</Label>
          {members.length > 30 && (
            <button onClick={() => setShowAllMembers(!showAllMembers)} style={{
              background: "none", border: "none", color: "var(--saffron)",
              fontSize: "0.95rem", cursor: "pointer", fontFamily: "'Inter',sans-serif",
            }}>
              {showAllMembers ? "Show less ↑" : `+${members.length - 30} more ↓`}
            </button>
          )}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          {visible.map((m, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, width: 52 }}>
              <SatsangAvatar name={m.display_name} photo={m.photo_url} size={44} />
              <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", textAlign: "center",
                maxWidth: 52, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {(m.display_name || "Seeker").split(" ")[0]}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* ── Most Blessed ── */}
      {topPost && (
        <div style={{
          borderRadius: 14, padding: "16px 18px",
          background: "linear-gradient(135deg,rgba(201,149,58,0.1),rgba(249,115,22,0.06))",
          border: "1px solid rgba(201,149,58,0.35)",
          boxShadow: "0 0 20px rgba(201,149,58,0.08)",
        }}>
          <div style={{ fontSize: "0.95rem", fontWeight: "700",
            color: "var(--gold)", fontFamily: "'Inter',sans-serif", marginBottom: 10 }}>
            Most blessed today
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <SatsangAvatar name={topPost.display_name} photo={topPost.photo_url} size={34} />
            <div>
              <div style={{ fontSize: "0.95rem", color: "var(--text)" }}>{topPost.display_name}</div>
              <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>Day {topPost.day}</div>
            </div>
          </div>
          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.05rem",
            color: "var(--text)", lineHeight: 1.6, marginBottom: 8 }}>
            "{topPost.text}"
          </div>
          <div style={{ fontSize: "0.95rem", color: "var(--saffron)" }}>🙏 {topPost.reactions} blessings received</div>
        </div>
      )}

      {/* ── Share your word ── */}
      <Card>
        <Label>Share with Your Satsang</Label>
        <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: 12 }}>
          One word or one line — what did today's verse bring up for you?
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={postText}
            onChange={e => setPostText(e.target.value.slice(0, 120))}
            placeholder="Gratitude... Surrender... Clarity..."
            style={{
              flex: 1, background: "var(--bg2)",
              border: "1px solid var(--border)", borderRadius: 10,
              padding: "10px 14px", color: "var(--text)",
              fontFamily: "'Inter',sans-serif", fontSize: "1rem", outline: "none",
            }}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && submitPost()}
          />
          <button onClick={submitPost} disabled={posting || !postText.trim()} style={{
            padding: "10px 16px", borderRadius: 10, border: "none",
            background: (!posting && postText.trim())
              ? "var(--saffron)"
              : "var(--bg3)",
            color: (!posting && postText.trim()) ? "#fff" : "var(--text-dim)",
            cursor: posting ? "not-allowed" : "pointer",
            fontFamily: "'Inter',sans-serif", fontSize: "1rem", fontWeight: "600",
            transition: "all 0.2s",
          }}>
            {posting ? "🙏" : "Share"}
          </button>
        </div>
      </Card>

      {/* ── Community Feed ── */}
      {posts.length > 0 && (
        <Card>
          <Label>Satsang Feed — Day {currentDay}</Label>
          <div style={{ display: "flex", flexDirection: "column", gap: 0, marginTop: 10 }}>
            {posts.map((p, idx) => (
              <div key={p.id} style={{
                display: "flex", gap: 10, alignItems: "flex-start",
                padding: "12px 0",
                borderBottom: idx < posts.length - 1 ? "1px solid var(--border)" : "none",
              }}>
                <SatsangAvatar name={p.display_name} photo={p.photo_url} size={36} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                    <span style={{ fontSize: "0.95rem", color: "var(--saffron)" }}>{p.display_name}</span>
                    <span style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>Day {p.day}</span>
                  </div>
                  <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1rem",
                    color: "var(--text)", lineHeight: 1.55, marginBottom: 7 }}>
                    {p.text}
                  </div>
                  <button onClick={() => toggleReact(p.id)} style={{
                    background: p.i_reacted ? "rgba(249,115,22,0.12)" : "transparent",
                    border: `1px solid ${p.i_reacted ? "rgba(249,115,22,0.4)" : "var(--border)"}`,
                    borderRadius: 20, padding: "3px 12px",
                    color: p.i_reacted ? "var(--saffron)" : "var(--text-dim)",
                    cursor: "pointer", fontSize: "0.95rem", transition: "all 0.2s",
                    transform: p.i_reacted ? "scale(1.05)" : "scale(1)",
                  }}>
                    🙏 {p.reactions > 0 ? p.reactions : "Bless"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ── LEGACY stub so old call-sites don't crash ─────────────────────────────────
function PodSection({ user, journeyType, currentDay }) {
  const [pod,      setPod]      = useState(null);
  const [posts,    setPosts]    = useState([]);
  const [postText, setPostText] = useState("");
  const [posting,  setPosting]  = useState(false);
  const [joining,  setJoining]  = useState(false);
  const [err,      setErr]      = useState(null);

  const userId = user?.id;
  const displayName = user?.name || user?.user_metadata?.full_name || user?.email?.split("@")[0] || "Seeker";

  useEffect(() => {
    if (!userId || !journeyType) return;
    setJoining(true);
    fetch(`${API}/challenge/join`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id:      userId,
        journey_type: journeyType,
        display_name: displayName,
        photo_url:    user?.user_metadata?.avatar_url || null,
      }),
    })
      .then(() => fetch(`${API}/challenge/pod/${encodeURIComponent(userId)}/${encodeURIComponent(journeyType)}`))
      .then(r => r.json())
      .then(d => {
        if (d.pod_id) {
          setPod(d);
          // posts come embedded in pod data already; also fetch with user_id for reaction state
          return fetch(`${API}/challenge/pod/posts/${d.pod_id}?day=${currentDay}&user_id=${encodeURIComponent(userId)}`);
        }
      })
      .then(r => r && r.json())
      .then(d => { if (d?.posts) setPosts(d.posts); })
      .catch(() => setErr("Could not load pod"))
      .finally(() => setJoining(false));
  }, [userId, journeyType]);

  const submitPost = async () => {
    if (!postText.trim() || !pod?.pod_id || posting) return;
    setPosting(true);
    try {
      const r = await fetch(`${API}/challenge/pod/post`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pod_id:       pod.pod_id,
          user_id:      userId,
          display_name: displayName,
          day:          currentDay,
          post_text:    postText.trim(),
        }),
      });
      const d = await r.json();
      if (d.posted) {
        const newPost = {
          id:           d.post_id,
          user_id:      userId,
          display_name: displayName,
          day:          currentDay,
          text:         postText.trim(),
          created_at:   new Date().toISOString(),
          reactions:    0,
          i_reacted:    false,
        };
        setPosts(prev => [newPost, ...prev]);
        setPostText("");
        // Update pod checked_in_today count
        setPod(prev => prev ? { ...prev, checked_in_today: (prev.checked_in_today || 0) + 1 } : prev);
      }
    } catch { /* non-critical */ }
    setPosting(false);
  };

  const toggleReact = async (postId) => {
    if (!userId) return;
    // Optimistic update
    setPosts(prev => prev.map(p => p.id === postId
      ? { ...p, reactions: p.i_reacted ? p.reactions - 1 : p.reactions + 1, i_reacted: !p.i_reacted }
      : p
    ));
    try {
      await fetch(`${API}/challenge/pod/react`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ post_id: postId, user_id: userId }),
      });
    } catch {
      // Revert on failure
      setPosts(prev => prev.map(p => p.id === postId
        ? { ...p, reactions: p.i_reacted ? p.reactions + 1 : p.reactions - 1, i_reacted: !p.i_reacted }
        : p
      ));
    }
  };

  if (joining) return (
    <div style={{ textAlign: "center", padding: "20px 0", color: "var(--text-dim)", fontSize: "0.95rem" }}>
      <div style={{ fontSize: "1.4rem", marginBottom: "8px" }}>🙏</div>
      Finding your pod...
    </div>
  );
  if (err) return <div style={{ color: "var(--text-dim)", fontSize: "0.9rem" }}>{err}</div>;
  if (!pod) return null;

  const members       = pod.members || [];
  const checkedIn     = pod.checked_in_today || 0;
  const total         = members.length || 10;
  const shaktiPct     = Math.round((checkedIn / total) * 100);
  const shaktiLabel   = pod.shakti_label || "Awakening 🌱";
  const podName       = pod.pod_name || `Pod #${pod.pod_number}`;

  // Top blessed post (most reactions, min 1)
  const topPost = [...posts].sort((a, b) => b.reactions - a.reactions).find(p => p.reactions > 0);

  return (
    <div>

      {/* Pod identity header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px",
      }}>
        <div>
          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "0.95rem", color: "var(--text)", marginBottom: "2px" }}>
            {podName}
          </div>
          <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
            {members.length} seekers · Cohort #{pod.cohort_id}
          </div>
        </div>
        <div style={{
          background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.35)",
          borderRadius: "20px", padding: "4px 12px", fontSize: "0.95rem", color: "var(--gold)",
          fontFamily: "'Inter',sans-serif",
        }}>
          Day {currentDay}
        </div>
      </div>

      {/* Shakti meter */}
      <div style={{
        padding: "14px 16px",
        background: "var(--bg2)",
        border: "1px solid var(--border)",
        borderRadius: "10px", marginBottom: "16px",
        boxShadow: "var(--card-shadow)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
            Pod Shakti
          </div>
          <div style={{ fontSize: "0.95rem", color: "var(--gold)", fontWeight: "600" }}>
            {checkedIn}/{total} · {shaktiLabel}
          </div>
        </div>
        <div style={{ background: "var(--bg3)", borderRadius: "6px", height: "8px", overflow: "hidden" }}>
          <div style={{
            width: `${shaktiPct}%`, height: "100%", borderRadius: "6px",
            background: "linear-gradient(90deg, var(--saffron), var(--gold))",
            transition: "width 0.8s ease",
            boxShadow: shaktiPct > 50 ? "0 0 8px rgba(212,175,55,0.6)" : "none",
          }} />
        </div>
        <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginTop: "6px" }}>
          {checkedIn === total
            ? "All seekers have shown up today — the pod is complete 🔥"
            : checkedIn === 0
            ? "Be the first to check in and light the flame for your pod."
            : `${total - checkedIn} seeker${total - checkedIn === 1 ? "" : "s"} yet to check in today.`
          }
        </div>
      </div>

      {/* Member avatars */}
      {members.length > 0 && (
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "16px" }}>
          {members.map((m, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
              <div style={{
                width: "38px", height: "38px", borderRadius: "50%", overflow: "hidden",
                border: "2px solid rgba(212,175,55,0.5)",
                background: "rgba(232,131,42,0.08)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {m.photo_url
                  ? <img src={m.photo_url} alt={m.display_name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <span style={{ fontSize: "1rem", color: "var(--saffron)", fontWeight: "700" }}>
                      {(m.display_name || "?")[0].toUpperCase()}
                    </span>
                }
              </div>
              <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", maxWidth: "52px", textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {m.display_name?.split(" ")[0] || "Seeker"}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Top blessed post */}
      {topPost && (
        <div style={{
          padding: "12px 14px", marginBottom: "14px",
          background: "var(--bg2)",
          border: "1px solid var(--border)",
          borderRadius: "10px",
          position: "relative",
          boxShadow: "var(--card-shadow)",
        }}>
          <div style={{ fontSize: "0.95rem", color: "var(--gold)", marginBottom: "6px" }}>
            🌟 Most Blessed Today
          </div>
          <div style={{ fontSize: "1rem", color: "var(--text)", marginBottom: "6px" }}>
            "{topPost.text}"
          </div>
          <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
            — {topPost.display_name?.split(" ")[0]} · 🙏 {topPost.reactions}
          </div>
        </div>
      )}

      {/* Share your word */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "8px" }}>
          Share one word or feeling with your pod:
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <input
            value={postText}
            onChange={e => setPostText(e.target.value)}
            onKeyDown={e => e.key === "Enter" && submitPost()}
            placeholder="Grateful · Lighter · Seen · Peaceful..."
            maxLength={200}
            style={{
              flex: 1, padding: "10px 14px", borderRadius: "8px",
              border: "1px solid var(--border)", background: "var(--bg2)",
              color: "var(--cream)", fontFamily: "'Inter',sans-serif", fontSize: "1rem",
              outline: "none",
            }}
          />
          <button
            onClick={submitPost}
            disabled={posting || !postText.trim()}
            style={{
              padding: "10px 18px", borderRadius: "8px",
              cursor: posting || !postText.trim() ? "default" : "pointer",
              background: "rgba(232,131,42,0.15)", border: "1px solid var(--saffron)",
              color: "var(--saffron)", fontFamily: "'Inter',sans-serif", fontSize: "1rem",
              opacity: posting || !postText.trim() ? 0.5 : 1, transition: "all 0.2s",
            }}
          >
            {posting ? "..." : "🙏"}
          </button>
        </div>
      </div>

      {/* Pod posts feed with reactions */}
      {posts.length > 0 && (
        <div>
          <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "10px" }}>
            From your pod today
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {posts.slice(0, 8).map((p, i) => (
              <div key={p.id || i} style={{
                display: "flex", alignItems: "flex-start", gap: "10px",
                padding: "10px 14px",
                background: p.i_reacted ? "rgba(232,131,42,0.06)" : "var(--bg2)",
                border: `1px solid ${p.i_reacted ? "rgba(212,175,55,0.45)" : "var(--border)"}`,
                borderRadius: "10px", transition: "all 0.2s",
              }}>
                {/* Avatar */}
                <div style={{
                  width: "30px", height: "30px", borderRadius: "50%", flexShrink: 0,
                  background: "rgba(232,131,42,0.1)", border: "1px solid rgba(232,131,42,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "0.9rem", color: "var(--saffron)", fontWeight: "700",
                }}>
                  {(p.display_name || "?")[0].toUpperCase()}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "3px" }}>
                    {p.display_name?.split(" ")[0] || "Seeker"}
                  </div>
                  <div style={{ fontSize: "0.97rem", color: "var(--text)", lineHeight: "1.5" }}>
                    {p.text}
                  </div>
                </div>

                {/* 🙏 reaction button */}
                <button
                  onClick={() => p.id && toggleReact(p.id)}
                  style={{
                    display: "flex", flexDirection: "column", alignItems: "center", gap: "2px",
                    padding: "6px 10px", borderRadius: "8px", cursor: "pointer",
                    border: `1px solid ${p.i_reacted ? "rgba(212,175,55,0.6)" : "rgba(212,175,55,0.2)"}`,
                    background: p.i_reacted ? "rgba(212,175,55,0.15)" : "transparent",
                    color: p.i_reacted ? "var(--gold)" : "var(--text-dim)",
                    transition: "all 0.2s", flexShrink: 0,
                    transform: p.i_reacted ? "scale(1.08)" : "scale(1)",
                  }}
                >
                  <span style={{ fontSize: "1rem" }}>🙏</span>
                  <span style={{ fontSize: "0.95rem", fontWeight: "600" }}>{p.reactions || 0}</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Transformation Graph ──────────────────────────────────────────────────────

function TransformationGraph({ points }) {
  if (!points || points.length < 2) return null;

  const W = 560, H = 160, PAD = 24;
  const innerW = W - PAD * 2;
  const innerH = H - PAD * 2;

  // Map day/score to svg coords
  const xOf = (day) => PAD + ((day - 1) / 20) * innerW;
  const yOf = (score) => PAD + ((5 - score) / 4) * innerH;

  const pathD = points.map((p, i) =>
    `${i === 0 ? "M" : "L"} ${xOf(p.day)} ${yOf(p.score)}`
  ).join(" ");

  // Area fill
  const areaD = `${pathD} L ${xOf(points[points.length - 1].day)} ${H - PAD} L ${xOf(points[0].day)} ${H - PAD} Z`;

  return (
    <div style={{ marginTop: "20px" }}>
      <Label>Transformation Arc — Daily Score (1–5)</Label>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible" }}>
        {/* Grid lines */}
        {[1, 2, 3, 4, 5].map(score => (
          <line key={score}
            x1={PAD} y1={yOf(score)} x2={W - PAD} y2={yOf(score)}
            stroke="rgba(201,149,58,0.1)" strokeWidth="1"
          />
        ))}
        {/* Score labels */}
        {[1, 3, 5].map(score => (
          <text key={score} x={PAD - 6} y={yOf(score) + 4}
            textAnchor="end" fill="rgba(201,149,58,0.5)" fontSize="12">
            {score}
          </text>
        ))}
        {/* Area fill */}
        <path d={areaD} fill="rgba(232,131,42,0.06)" />
        {/* Line */}
        <path d={pathD} fill="none" stroke="var(--saffron)" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" />
        {/* Dots */}
        {points.map(p => (
          <circle key={p.day} cx={xOf(p.day)} cy={yOf(p.score)} r="4"
            fill="var(--saffron)" stroke="var(--bg)" strokeWidth="2" />
        ))}
        {/* Day labels */}
        {[1, 7, 14, 21].map(day => (
          <text key={day} x={xOf(day)} y={H} textAnchor="middle"
            fill="rgba(201,149,58,0.5)" fontSize="12">
            {day}
          </text>
        ))}
      </svg>
      <div style={{ display: "flex", gap: "16px", marginTop: "12px" }}>
        {points.slice(-3).map(p => (
          <div key={p.day} style={{
            background: "rgba(232,131,42,0.08)", border: "1px solid var(--border)",
            borderRadius: "8px", padding: "8px 14px", fontSize: "1rem",
          }}>
            <span style={{ color: "var(--text-dim)" }}>Day {p.day} </span>
            <span style={{ color: "var(--saffron)" }}>{"★".repeat(p.score)}{"☆".repeat(5 - p.score)}</span>
            <span style={{ color: "var(--text-dim)", fontSize: "1rem" }}> · {p.emotion}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Emotion Heatmap ───────────────────────────────────────────────────────────

const EMOTION_COLOR = (emotion = "") => {
  const e = emotion.toLowerCase();
  if (/overwhelm|anxious|anxiet|afraid|fear|heavy|dread|panic/.test(e)) return "#c2410c";
  if (/angry|anger|resent|frustrat|rage/.test(e))                        return "#c0392b";
  if (/stuck|lost|uncertain|confused|hopeless|numb/.test(e))             return "#a16207";
  if (/hopeful|curious|lighter|seeking|open/.test(e))                    return "#0f766e";
  if (/peace|clear|grateful|calm|serene|at peace|steady/.test(e))        return "#15803d";
  if (/sad|grief|mourn|hurt|lonely/.test(e))                             return "#4338ca";
  return "var(--saffron)";
};

const EMOTION_LEGEND = [
  { label: "Anxious / Heavy", color: "#c2410c" },
  { label: "Angry",           color: "#c0392b" },
  { label: "Stuck / Lost",    color: "#a16207" },
  { label: "Hopeful",         color: "#0f766e" },
  { label: "At Peace",        color: "#15803d" },
  { label: "Grief / Sad",     color: "#4338ca" },
];

function EmotionHeatmap({ points = [], checkedDays = [], currentDay = 1 }) {
  const [tooltip, setTooltip] = useState(null);

  const dayMap = {};
  points.filter(p => !p.is_grace).forEach(p => { dayMap[p.day] = p; });

  return (
    <div style={{ marginTop: "20px" }}>
      <Label>Emotion Heatmap — 21 Days</Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "8px" }}>
        {Array.from({ length: 21 }, (_, i) => i + 1).map(day => {
          const done  = checkedDays.includes(day);
          const today = day === currentDay && !done;
          const data  = dayMap[day];
          const color = data ? EMOTION_COLOR(data.emotion) : null;

          return (
            <div
              key={day}
              onMouseEnter={() => data && setTooltip(day)}
              onMouseLeave={() => setTooltip(null)}
              style={{
                position:      "relative",
                aspectRatio:   "1",
                borderRadius:  "8px",
                background:    done && color ? `${color}26` : "var(--bg3)",
                border:        today
                  ? "1px solid var(--saffron)"
                  : done
                  ? `1px solid ${color}55`
                  : "1px solid var(--border)",
                display:         "flex",
                flexDirection:   "column",
                alignItems:      "center",
                justifyContent:  "center",
                cursor:          data ? "pointer" : "default",
                transition:      "all 0.2s",
              }}
            >
              <div style={{
                fontSize:   "0.9rem",
                fontWeight: "600",
                color:      done ? color : today ? "var(--saffron)" : "var(--text-dim)",
              }}>{day}</div>

              {data && (
                <div style={{ display: "flex", gap: "2px", marginTop: "3px" }}>
                  {Array.from({ length: 5 }, (_, i) => (
                    <div key={i} style={{
                      width: "3px", height: "3px", borderRadius: "50%",
                      background: i < data.score ? color : "var(--border)",
                    }} />
                  ))}
                </div>
              )}

              {tooltip === day && data && (
                <div style={{
                  position:  "absolute",
                  bottom:    "calc(100% + 8px)",
                  left:      "50%",
                  transform: "translateX(-50%)",
                  background: "var(--bg2)",
                  border:    `1px solid ${color}66`,
                  borderRadius: "8px",
                  padding:   "8px 12px",
                  whiteSpace: "nowrap",
                  zIndex:    20,
                  fontSize:  "0.95rem",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
                  pointerEvents: "none",
                }}>
                  <div style={{ color, fontWeight: "600", marginBottom: "2px" }}>Day {day}</div>
                  <div style={{ color: "var(--text)", marginBottom: "2px" }}>{data.emotion}</div>
                  <div style={{ color: "var(--saffron)" }}>
                    {"★".repeat(data.score)}{"☆".repeat(5 - data.score)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 16px", marginTop: "14px" }}>
        {EMOTION_LEGEND.map(({ label, color }) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.9rem", color: "var(--text-dim)" }}>
            <div style={{ width: "10px", height: "10px", borderRadius: "3px", background: color, opacity: 0.75, flexShrink: 0 }} />
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Journey Selection ─────────────────────────────────────────────────────────

function JourneySelection({ onStart }) {
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [starting, setStarting] = useState(null);

  useEffect(() => {
    fetch(`${API}/journey/list`)
      .then(r => r.json())
      .then(d => { setJourneys(d.journeys || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ color: "var(--text-dim)", padding: "40px", textAlign: "center" }}>
      🕉️ Loading journeys...
    </div>
  );

  return (
    <div className="page">
      <div className="section-title">21-Day Sankalpa Challenge</div>
      <div className="section-sub">A vow, not a plan. One verse, one practice, one day at a time — for 21 days.</div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px,1fr))", gap: "16px" }}>
        {journeys.map(j => (
          <div key={j.id} style={{
            background: "var(--bg2)",
            border: "1px solid var(--border)", borderRadius: "var(--radius)",
            padding: "24px", cursor: "pointer", transition: "all 0.25s",
            position: "relative", overflow: "hidden",
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--saffron)"; e.currentTarget.style.background = "linear-gradient(135deg,var(--bg2),rgba(232,131,42,0.06))"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--bg2)"; }}
          >
            <div style={{ fontSize: "2.2rem", marginBottom: "12px" }}>{j.icon}</div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1rem", color: "var(--saffron)", marginBottom: "6px" }}>
              {j.title}
            </div>
            <div style={{ fontSize: "1rem", color: "var(--text-dim)", marginBottom: "20px", lineHeight: "1.5" }}>
              {j.subtitle}
            </div>
            <Btn
              onClick={() => { setStarting(j.id); onStart(j.id); }}
              disabled={starting === j.id}
              style={{ width: "100%", justifyContent: "center" }}
            >
              {starting === j.id ? "Starting..." : "Take Your Sankalpa →"}
            </Btn>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: "32px", padding: "20px 24px",
        background: "rgba(232,131,42,0.05)", border: "1px solid rgba(232,131,42,0.15)",
        borderRadius: "var(--radius)", fontSize: "1rem", color: "var(--text-dim)", lineHeight: "1.8",
      }}>
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1rem", fontWeight: "700", color: "var(--gold)", marginBottom: "14px" }}>How the sankalpa works</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px,1fr))", gap: "12px" }}>
          {[
            { week: "Every Day", desc: "One curated Gita verse matched to your struggle. You choose your emotion. You rate how you feel after." },
            { week: "Week 1 · Pranayama", desc: "Each day begins with Alternate Nostril Breathing — 5 rounds — to calm the mind before the verse lands." },
            { week: "Week 2 · Journaling", desc: "A specific prompt each day. Write without stopping. Week 2 is where the real turning begins." },
            { week: "Week 3 · Sadhana", desc: "One concrete act of practice each day. Week 3 is integration — the verse becomes your life." },
            { week: "Day 5 · Seva",  desc: "Your Day 5 practice is to share this sankalpa with 3 people. This is how it spreads." },
            { week: "Day 21 · Earned", desc: "Completers receive an earned practice — given only to those who see all 21 days through." },
          ].map(item => (
            <div key={item.week} style={{ padding: "12px 14px", background: "rgba(232,131,42,0.04)", border: "1px solid rgba(232,131,42,0.15)", borderRadius: "8px" }}>
              <div style={{ fontSize: "0.95rem", color: "var(--saffron)", fontWeight: "600", marginBottom: "4px" }}>{item.week}</div>
              <div style={{ fontSize: "0.92rem", color: "var(--text-dim)", lineHeight: "1.55" }}>{item.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Journey Companion Chat ────────────────────────────────────────────────────

function CompanionView({ user, journeyId, currentDay }) {
  const [thread,   setThread]   = useState(null);
  const [input,    setInput]    = useState("");
  const [sending,  setSending]  = useState(false);
  const [patterns, setPatterns] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => { loadThread(); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const loadThread = async () => {
    try {
      const r = await fetch(`${API}/companion/thread/${user.id}/${journeyId}`);
      const d = await r.json();
      setThread(d.thread || []);
      setPatterns(d.patterns || null);
    } catch { setThread([]); }
  };

  const send = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput("");
    setSending(true);
    // Optimistic — show user message immediately
    setThread(prev => [...(prev || []), { role: "user", message: text, day: currentDay, type: "chat", id: Date.now() }]);
    try {
      const r = await fetch(`${API}/companion/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id, journey_id: journeyId, message: text }),
      });
      const d = await r.json();
      if (d.response) {
        setThread(prev => [...(prev || []), { role: "companion", message: d.response, day: currentDay, type: "chat", id: Date.now() + 1 }]);
      }
    } catch { /* keep optimistic */ }
    setSending(false);
  };

  const handleKey = e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  const isEmpty = !thread || thread.length === 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 220px)", minHeight: "400px" }}>

      {/* Pattern bar — only show after Day 3 */}
      {patterns && patterns.days_done >= 3 && (
        <div style={{
          display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px",
          padding: "14px 18px", background: "rgba(232,131,42,0.06)",
          border: "1px solid rgba(232,131,42,0.15)", borderRadius: "var(--radius)",
          fontSize: "1.05rem", color: "var(--text-dim)",
        }}>
          <span>📊 <strong style={{ color: "var(--saffron)" }}>{patterns.days_done}</strong> days done</span>
          <span>⭐ avg <strong style={{ color: "var(--saffron)" }}>{patterns.avg_score}/5</strong></span>
          <span>Most felt: <strong style={{ color: "var(--cream)" }}>{patterns.top_emotion}</strong></span>
          <span>Trend: <strong style={{ color: patterns.trajectory === "rising" ? "#15803d" : patterns.trajectory === "falling" ? "#dc2626" : "var(--gold)" }}>
            {patterns.trajectory === "rising" ? "↑ rising" : patterns.trajectory === "falling" ? "↓ falling" : "~ mixed"}
          </strong></span>
        </div>
      )}

      {/* Thread */}
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "16px", paddingBottom: "8px", scrollbarWidth: "thin", scrollbarColor: "var(--border) transparent" }}>

        {isEmpty && (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-dim)" }}>
            <div style={{ fontSize: "2rem", marginBottom: "12px" }}>🕯️</div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.05rem", color: "var(--gold)", marginBottom: "8px" }}>
              Your companion has been watching
            </div>
            <div style={{ fontSize: "1rem", lineHeight: "1.7" }}>
              Every day you check in, every verse you read — all of it is here.<br />
              What is on your mind today?
            </div>
          </div>
        )}

        {(thread || []).map((msg, i) => {
          const isCompanion = msg.role === "companion";
          const isSummary   = msg.type === "weekly_summary";
          return (
            <div key={msg.id || i} style={{ display: "flex", gap: "10px", flexDirection: isCompanion ? "row" : "row-reverse", animation: "fadein 0.3s ease" }}>
              {isCompanion && (
                <div style={{
                  width: "34px", height: "34px", borderRadius: "50%", flexShrink: 0,
                  background: isSummary
                    ? "linear-gradient(135deg,#6366f1,#8b5cf6)"
                    : "var(--bg2)",
                  border: "1px solid rgba(201,149,58,0.4)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "1rem",
                }}>🕯️</div>
              )}
              <div style={{
                maxWidth: "75%",
                background: isCompanion
                  ? (isSummary ? "linear-gradient(135deg,rgba(99,102,241,0.12),rgba(139,92,246,0.08))" : "var(--bg2)")
                  : "rgba(232,131,42,0.1)",
                border: isCompanion
                  ? (isSummary ? "1px solid rgba(99,102,241,0.3)" : "1px solid var(--border)")
                  : "1px solid rgba(232,131,42,0.25)",
                borderRadius: isCompanion ? "4px 12px 12px 12px" : "12px 4px 12px 12px",
                padding: "12px 16px",
              }}>
                {isSummary && (
                  <div style={{ fontSize: "0.95rem", color: "#6d28d9", marginBottom: "6px" }}>
                    Week {Math.ceil(msg.day / 7)} Summary · Day {msg.day}
                  </div>
                )}
                {!isSummary && isCompanion && (
                  <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "4px" }}>
                    Day {msg.day}
                  </div>
                )}
                <div style={{ fontSize: "1.05rem", lineHeight: "1.75", color: isCompanion ? "var(--text)" : "var(--cream)", fontFamily: "'Inter',sans-serif" }}>
                  {msg.message}
                </div>
              </div>
            </div>
          );
        })}

        {sending && (
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ width: "34px", height: "34px", borderRadius: "50%", background: "var(--bg2)", border: "1px solid rgba(201,149,58,0.4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem" }}>🕯️</div>
            <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "4px 12px 12px 12px", padding: "14px 18px" }}>
              <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{ width: "6px", height: "6px", borderRadius: "50%", background: "var(--saffron)", animation: `typing-dot 1.2s ${i * 0.2}s infinite` }} />
                ))}
              </div>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ paddingTop: "12px", borderTop: "1px solid var(--border)", marginTop: "8px" }}>
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
          <textarea
            value={input}
            onChange={e => { setInput(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 100) + "px"; }}
            onKeyDown={handleKey}
            placeholder="What's on your mind today?"
            rows={1}
            style={{
              flex: 1, background: "var(--bg3)", border: "1px solid var(--border)",
              borderRadius: "10px", padding: "10px 14px", color: "var(--text)",
              fontFamily: "'Inter',sans-serif", fontSize: "1.05rem", resize: "none",
              outline: "none", lineHeight: "1.5", transition: "border-color 0.2s",
            }}
          />
          <button onClick={send} disabled={!input.trim() || sending} style={{
            width: "40px", height: "40px", borderRadius: "50%", border: "none", flexShrink: 0,
            background: input.trim() ? "var(--saffron)" : "var(--bg3)",
            color: input.trim() ? "white" : "var(--text-dim)",
            cursor: input.trim() ? "pointer" : "default", fontSize: "1rem", transition: "all 0.2s",
          }}>🙏</button>
        </div>
      </div>
    </div>
  );
}

// ── Already Done Today ────────────────────────────────────────────────────────

// ── Satsang Group — Community Layer ──────────────────────────────────────────

function Avatar({ name = "", photoUrl = "", size = 36 }) {
  const initials = name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";
  return photoUrl ? (
    <img src={photoUrl} alt={name} style={{
      width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0,
    }} />
  ) : (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: "linear-gradient(135deg,rgba(232,131,42,0.3),rgba(201,149,58,0.2))",
      border: "1px solid rgba(232,131,42,0.4)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.38, color: "var(--saffron)", fontWeight: "700",
    }}>
      {initials}
    </div>
  );
}

function MemberProfileModal({ userId, onClose }) {
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetch(`${API}/satsang/member-profile/${userId}`)
      .then(r => r.json())
      .then(d => setProfile(d))
      .catch(() => {});
  }, [userId]);

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "20px",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--bg2)", border: "1px solid var(--border)",
        borderRadius: "16px", padding: "28px", width: "100%", maxWidth: "320px",
        position: "relative",
      }}>
        <button onClick={onClose} style={{
          position: "absolute", top: "14px", right: "14px",
          background: "none", border: "none", color: "var(--text-dim)",
          cursor: "pointer", fontSize: "1.1rem",
        }}>✕</button>

        {!profile ? (
          <div style={{ textAlign: "center", color: "var(--text-dim)", padding: "20px" }}>Loading...</div>
        ) : profile.error ? (
          <div style={{ textAlign: "center", color: "var(--text-dim)", padding: "20px" }}>Profile not found</div>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "20px" }}>
              <Avatar name={profile.display_name} photoUrl={profile.photo_url} size={64} />
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.1rem", color: "var(--cream)", marginTop: "12px" }}>
                {profile.display_name}
              </div>
            </div>

            {profile.journey_label ? (
              <div style={{
                background: "rgba(232,131,42,0.08)", border: "1px solid rgba(232,131,42,0.2)",
                borderRadius: "10px", padding: "14px 16px",
              }}>
                <div style={{ fontSize: "0.9rem", color: "var(--saffron)", marginBottom: "10px" }}>
                  Journey
                </div>
                <div style={{ color: "var(--cream)", fontSize: "1rem", marginBottom: "10px" }}>
                  {profile.journey_label}
                </div>
                <div style={{ display: "flex", gap: "20px" }}>
                  <div>
                    <div style={{ fontSize: "1.4rem", color: "var(--saffron)", fontFamily: "'Inter',sans-serif" }}>
                      {profile.days_done}
                    </div>
                    <div style={{ fontSize: "0.9rem", color: "var(--text-dim)" }}>days done</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "1.4rem", color: "var(--saffron)", fontFamily: "'Inter',sans-serif" }}>
                      {profile.completed ? "✓" : `${profile.current_day}/21`}
                    </div>
                    <div style={{ fontSize: "0.9rem", color: "var(--text-dim)" }}>{profile.completed ? "completed" : "progress"}</div>
                  </div>
                </div>

                {/* Day progress dots */}
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", marginTop: "12px" }}>
                  {Array.from({ length: 21 }, (_, i) => (
                    <div key={i} style={{
                      width: "10px", height: "10px", borderRadius: "50%",
                      background: i < profile.days_done ? "var(--saffron)" : "var(--bg)",
                      border: "1px solid var(--border)",
                    }} />
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: "center", color: "var(--text-dim)", fontSize: "0.9rem" }}>
                No active journey yet
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PastAnswers({ answers }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      marginBottom: "14px",
      background: "var(--bg3)",
      border: "1px dashed var(--border)",
      borderRadius: "10px",
      overflow: "hidden",
    }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 14px", background: "none", border: "none", cursor: "pointer",
        color: "var(--text-dim)", fontSize: "0.92rem", }}>
        <span>From past journeys ({answers.length})</span>
        <span style={{ fontSize: "0.92rem" }}>{open ? "▲ hide" : "▼ show"}</span>
      </button>
      {open && (
        <div style={{ padding: "0 14px 12px", borderTop: "1px dashed var(--border)" }}>
          {answers.map(a => {
            const cleanName = a.display_name?.replace(" (past member)", "") || "Member";
            return (
              <div key={a.answer_id} style={{ marginTop: "12px", opacity: 0.65 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                  <Avatar name={cleanName} size={24} />
                  <span style={{ fontSize: "0.92rem", color: "var(--text-dim)" }}>{cleanName}</span>
                </div>
                <div style={{ fontSize: "0.9rem", color: "var(--cream)", lineHeight: "1.5" }}>
                  "{a.answer_text}"
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function QuestionThread({ q, groupId, user, onRefresh }) {
  const [replyTo,    setReplyTo]    = useState(null);
  const [replyText,  setReplyText]  = useState("");
  const [answerText, setAnswerText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showReplies, setShowReplies] = useState({});

  const deleteQuestion = async () => {
    if (!window.confirm("Delete your question?")) return;
    await fetch(`${API}/satsang/question/${q.question_id}/${user.id}`, { method: "DELETE" });
    onRefresh();
  };

  const deleteAnswer = async (answerId) => {
    if (!window.confirm("Delete your answer?")) return;
    await fetch(`${API}/satsang/answer/${answerId}/${user.id}`, { method: "DELETE" });
    onRefresh();
  };

  const deleteReply = async (replyId) => {
    if (!window.confirm("Delete your reply?")) return;
    await fetch(`${API}/satsang/reply/${replyId}/${user.id}`, { method: "DELETE" });
    onRefresh();
  };

  const submitAnswer = async () => {
    if (!answerText.trim() || submitting) return;
    setSubmitting(true);
    try {
      await fetch(`${API}/satsang/answer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question_id: q.question_id,
          user_id: user.id,
          answer_text: answerText.trim(),
          display_name: user?.name || user?.email || "",
          photo_url: user?.photo || "",
        }),
      });
      setAnswerText("");
      onRefresh();
    } catch { /* non-critical */ }
    setSubmitting(false);
  };

  const submitReply = async (answerId) => {
    if (!replyText.trim() || submitting) return;
    setSubmitting(true);
    try {
      await fetch(`${API}/satsang/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          answer_id: answerId,
          user_id: user.id,
          reply_text: replyText.trim(),
          display_name: user?.name || user?.email || "",
          photo_url: user?.photo || "",
        }),
      });
      setReplyText("");
      setReplyTo(null);
      onRefresh();
    } catch { /* non-critical */ }
    setSubmitting(false);
  };

  const isAiQ = q.question_type === "ai_weekly";
  const userAnswered = q.answers.some(a => a.user_id === user?.id);

  return (
    <div style={{
      border: `1px solid ${isAiQ ? "rgba(232,131,42,0.35)" : "var(--border)"}`,
      borderRadius: "12px",
      background: isAiQ ? "rgba(232,131,42,0.04)" : "var(--bg2)",
      padding: "18px",
      marginBottom: "16px",
    }}>
      {/* Question header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "14px" }}>
        {isAiQ ? (
          <div style={{
            width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
            background: "rgba(232,131,42,0.15)", border: "1px solid var(--saffron)",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem",
          }}>🪷</div>
        ) : (
          <Avatar name={q.poster_name || q.posted_by} photoUrl={q.poster_photo} size={36} />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: "0.95rem", color: isAiQ ? "var(--saffron)" : "var(--text-dim)", fontWeight: isAiQ ? "700" : "400" }}>
              {isAiQ ? "Gita Companion · this week" : (q.poster_name || "Member")}
            </div>
            {/* Delete button — only for own user questions */}
            {!isAiQ && q.posted_by === user?.id && (
              <button onClick={deleteQuestion} style={{
                background: "none", border: "none", cursor: "pointer",
                color: "var(--text-dim)", fontSize: "0.9rem", padding: "0 2px",
                opacity: 0.6,
              }} title="Delete question">✕</button>
            )}
          </div>
          <div style={{ color: "var(--cream)", fontSize: "1.05rem", lineHeight: "1.5", marginTop: "4px" }}>
            {q.question_text}
          </div>
        </div>
      </div>

      {/* Answers — split past vs active */}
      {q.answers.length > 0 && (() => {
        const pastAnswers   = q.answers.filter(a => a.display_name?.includes("(past member)"));
        const activeAnswers = q.answers.filter(a => !a.display_name?.includes("(past member)"));
        return (
          <>
            {/* Past member answers — collapsed inspiration */}
            {pastAnswers.length > 0 && (
              <PastAnswers answers={pastAnswers} />
            )}

            {/* Active group discussion */}
            {activeAnswers.length > 0 && (
              <div style={{ marginBottom: "12px" }}>
                <div style={{ fontSize: "0.9rem", color: "var(--text-dim)", marginBottom: "10px" }}>
                  Your group
                </div>
                <div style={{ marginLeft: "8px", borderLeft: "2px solid rgba(232,131,42,0.3)", paddingLeft: "14px" }}>
                  {activeAnswers.map(a => {
                    const cleanName = a.display_name || "Member";
                    return (
                    <div key={a.answer_id} style={{ marginBottom: "14px" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <Avatar name={a.user_id === "ai" ? "AI" : cleanName} photoUrl={a.photo_url} size={28} />
                          <span style={{ fontSize: "0.9rem", color: a.user_id === "ai" ? "var(--saffron)" : "var(--cream)" }}>
                            {a.user_id === "ai" ? "Gita Companion" : cleanName}
                          </span>
                        </div>
                        {a.user_id === user?.id && (
                          <button onClick={() => deleteAnswer(a.answer_id)} style={{
                            background: "none", border: "none", cursor: "pointer",
                            color: "var(--text-dim)", fontSize: "0.92rem", opacity: 0.6,
                          }} title="Delete">✕</button>
                        )}
                      </div>
              <div style={{ color: "var(--cream)", fontSize: "0.98rem", lineHeight: "1.55", marginBottom: "6px" }}>
                {a.answer_text}
              </div>

              {/* Replies */}
              {a.replies.length > 0 && (
                <div style={{ marginLeft: "12px", borderLeft: "1px solid var(--border)", paddingLeft: "12px", marginBottom: "8px" }}>
                  {(!showReplies[a.answer_id] ? a.replies.slice(0, 1) : a.replies).map((r, ri) => (
                    <div key={ri} style={{ marginBottom: "6px" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "3px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <Avatar name={r.display_name || r.user_id} photoUrl={r.photo_url} size={22} />
                          <span style={{ fontSize: "0.92rem", color: "var(--text-dim)" }}>{r.display_name || "Member"}</span>
                        </div>
                        {r.user_id === user?.id && (
                          <button onClick={() => deleteReply(r.reply_id)} style={{
                            background: "none", border: "none", cursor: "pointer",
                            color: "var(--text-dim)", fontSize: "0.95rem", opacity: 0.6,
                          }} title="Delete">✕</button>
                        )}
                      </div>
                      <div style={{ color: "var(--cream)", fontSize: "0.9rem", lineHeight: "1.45" }}>{r.reply_text}</div>
                    </div>
                  ))}
                  {a.replies.length > 1 && (
                    <button onClick={() => setShowReplies(p => ({ ...p, [a.answer_id]: !p[a.answer_id] }))} style={{
                      background: "none", border: "none", color: "var(--saffron)", cursor: "pointer", fontSize: "0.95rem", padding: "2px 0",
                    }}>
                      {showReplies[a.answer_id] ? "Hide replies" : `+${a.replies.length - 1} more`}
                    </button>
                  )}
                </div>
              )}

              {/* Reply input */}
              {replyTo === a.answer_id ? (
                <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                  <input
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    placeholder="Reply..."
                    style={{
                      flex: 1, padding: "7px 12px", borderRadius: "8px", fontSize: "0.9rem",
                      background: "var(--bg)", border: "1px solid var(--border)", color: "var(--cream)",
                    }}
                    onKeyDown={e => e.key === "Enter" && submitReply(a.answer_id)}
                  />
                  <button onClick={() => submitReply(a.answer_id)} disabled={submitting} style={{
                    padding: "7px 14px", borderRadius: "8px", cursor: "pointer",
                    background: "rgba(232,131,42,0.15)", border: "1px solid var(--saffron)",
                    color: "var(--saffron)", fontSize: "0.9rem",
                  }}>Send</button>
                  <button onClick={() => setReplyTo(null)} style={{
                    padding: "7px 10px", borderRadius: "8px", cursor: "pointer",
                    background: "transparent", border: "1px solid var(--border)", color: "var(--text-dim)", fontSize: "0.9rem",
                  }}>✕</button>
                </div>
              ) : (
                <button onClick={() => { setReplyTo(a.answer_id); setReplyText(""); }} style={{
                  background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer",
                  fontSize: "0.92rem", padding: "2px 0",
                }}>
                  Reply
                </button>
                  )}
                </div>
                );
              })}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Answer input */}
      {!userAnswered && (
        <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
          <input
            value={answerText}
            onChange={e => setAnswerText(e.target.value)}
            placeholder="Share your honest answer..."
            style={{
              flex: 1, padding: "9px 14px", borderRadius: "8px", fontSize: "0.98rem",
              background: "var(--bg)", border: "1px solid var(--border)", color: "var(--cream)",
            }}
            onKeyDown={e => e.key === "Enter" && submitAnswer()}
          />
          <button onClick={submitAnswer} disabled={submitting || !answerText.trim()} style={{
            padding: "9px 16px", borderRadius: "8px", cursor: submitting ? "default" : "pointer",
            background: "rgba(232,131,42,0.15)", border: "1px solid var(--saffron)",
            color: "var(--saffron)", fontSize: "0.98rem", opacity: (!answerText.trim() || submitting) ? 0.5 : 1,
          }}>Answer</button>
        </div>
      )}
    </div>
  );
}

function SatsangGroupSection({ user, journeyType }) {
  const [group,       setGroup]       = useState(null);
  const [feed,        setFeed]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [askText,     setAskText]     = useState("");
  const [asking,      setAsking]      = useState(false);
  const [showAsk,     setShowAsk]     = useState(false);
  const [viewProfile, setViewProfile] = useState(null);

  const displayName = user?.name || user?.email || "";
  const photoUrl    = user?.photo || "";

  const loadGroup = async () => {
    try {
      // Auto-join (idempotent)
      await fetch(`${API}/satsang/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          journey_type: journeyType,
          language: "en",
          display_name: displayName,
          photo_url: photoUrl,
        }),
      });
      // Fetch group + feed
      const r = await fetch(`${API}/satsang/group/${user.id}/${journeyType}`);
      const d = await r.json();
      setGroup(d.group);
      setFeed(d.feed || []);
    } catch { /* non-critical */ }
    setLoading(false);
  };

  useEffect(() => { if (user?.id && journeyType) loadGroup(); }, [user?.id, journeyType]); // eslint-disable-line

  const postQuestion = async () => {
    if (!askText.trim() || asking || !group) return;
    setAsking(true);
    try {
      await fetch(`${API}/satsang/question`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          group_id: group.group_id,
          user_id: user.id,
          question_text: askText.trim(),
          display_name: displayName,
          photo_url: photoUrl,
        }),
      });
      setAskText("");
      setShowAsk(false);
      await loadGroup();
    } catch { /* non-critical */ }
    setAsking(false);
  };

  if (loading) {
    return (
      <div style={{ padding: "20px", textAlign: "center", color: "var(--text-dim)" }}>
        Finding your group...
      </div>
    );
  }

  if (!group) return null;

  const isForming = group.status === "forming";
  const memberCount = (group.members || []).filter(m => !m.is_ai).length;

  return (
    <div>
      {/* Group header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: "16px",
      }}>
        <div>
          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1rem", color: "var(--saffron)", marginBottom: "2px" }}>
            Your Satsang Group
          </div>
          <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
            {isForming
              ? `Forming — ${memberCount} joined so far · Starts ${group.start_date}`
              : `${memberCount} seekers · Week ${group.week_number}`
            }
          </div>
        </div>

        {/* Member avatars — real users first, tappable */}
        {(() => {
          const realMembers = (group.members || []).filter(m => !m.is_ai && !m.user_id.startsWith("seed_"));
          const display = realMembers.slice(0, 6);
          return (
            <div style={{ display: "flex" }}>
              {display.map((m, i) => (
                <div
                  key={i}
                  onClick={() => setViewProfile(m.user_id)}
                  style={{
                    marginLeft: i > 0 ? "-8px" : 0,
                    zIndex: display.length - i,
                    cursor: "pointer",
                    transition: "transform 0.15s",
                  }}
                  title={`View ${m.display_name}'s profile`}
                >
                  <Avatar name={m.display_name || m.user_id} photoUrl={m.photo_url} size={32} />
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {isForming && (
        <div style={{
          padding: "14px 16px", borderRadius: "10px", marginBottom: "16px",
          background: "rgba(232,131,42,0.07)", border: "1px dashed rgba(232,131,42,0.3)",
          fontSize: "0.95rem", color: "var(--text-dim)", textAlign: "center",
        }}>
          Your group is forming. It starts on <strong style={{ color: "var(--saffron)" }}>{group.start_date}</strong> (next Monday).<br />
          Community discussions will open then.
        </div>
      )}

      {!isForming && (
        <>
          {/* Feed */}
          {feed.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center", color: "var(--text-dim)", fontSize: "0.95rem" }}>
              No questions yet — be the first to ask.
            </div>
          ) : (
            feed.map(q => (
              <QuestionThread key={q.question_id} q={q} groupId={group.group_id} user={user} onRefresh={loadGroup} />
            ))
          )}

          {/* Ask a question */}
          {showAsk ? (
            <div style={{ marginTop: "8px" }}>
              <textarea
                value={askText}
                onChange={e => setAskText(e.target.value)}
                placeholder="What's on your mind from this journey? Ask your group..."
                rows={3}
                style={{
                  width: "100%", padding: "12px 14px", borderRadius: "10px", fontSize: "0.98rem",
                  background: "var(--bg)", border: "1px solid var(--border)", color: "var(--cream)",
                  resize: "none", boxSizing: "border-box", lineHeight: "1.5",
                }}
              />
              <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
                <Btn onClick={postQuestion} disabled={asking || !askText.trim()}>
                  {asking ? "Posting..." : "Post Question"}
                </Btn>
                <Btn outline onClick={() => { setShowAsk(false); setAskText(""); }}>Cancel</Btn>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAsk(true)} style={{
              width: "100%", padding: "12px", borderRadius: "10px", cursor: "pointer",
              background: "transparent", border: "1px dashed var(--border)",
              color: "var(--text-dim)", fontSize: "0.95rem", fontFamily: "'Inter',sans-serif",
              textAlign: "left", marginTop: "4px",
            }}>
              + Ask your group a question...
            </button>
          )}
        </>
      )}

      {/* Member profile modal */}
      {viewProfile && (
        <MemberProfileModal userId={viewProfile} onClose={() => setViewProfile(null)} />
      )}
    </div>
  );
}

// ── Your Gita — personal 21-day verse map ────────────────────────────────────

function YourGitaView({ status, graphData, user }) {
  const checkins = status?.checkins || [];
  const checkinMap = {};
  checkins.forEach(c => { checkinMap[c.day] = c; });

  const scoreColor = s => {
    if (!s) return "var(--bg3)";
    if (s >= 5) return "var(--saffron)";
    if (s >= 4) return "var(--gold)";
    if (s >= 3) return "rgba(201,149,58,0.5)";
    if (s >= 2) return "rgba(201,149,58,0.25)";
    return "var(--text-dim)";
  };

  // Top-3 emotions
  const emotionCounts = {};
  checkins.forEach(c => { if (c.emotion) emotionCounts[c.emotion] = (emotionCounts[c.emotion] || 0) + 1; });
  const topEmotions = Object.entries(emotionCounts).sort((a, b) => b[1] - a[1]).slice(0, 3);

  return (
    <div className="page">
      <div style={{ textAlign: "center", marginBottom: "24px" }}>
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.3rem", color: "var(--saffron)", marginBottom: "6px" }}>
          Your Gita · 21 Days
        </div>
        <div style={{ fontSize: "1rem", color: "var(--text-dim)" }}>
          Every verse the Gita gave you. Every emotion you brought to it.
        </div>
      </div>

      {/* Arc bar */}
      <div style={{ display: "flex", gap: "3px", marginBottom: "24px", borderRadius: "6px", overflow: "hidden" }}>
        {Array.from({ length: 21 }, (_, i) => i + 1).map(day => {
          const ci = checkinMap[day];
          return (
            <div key={day} style={{
              flex: 1, height: "10px", borderRadius: "2px",
              background: ci ? scoreColor(ci.score) : "var(--bg3)",
              border: ci ? "none" : "1px solid var(--border)",
              transition: "background 0.3s",
            }} title={ci ? `Day ${day}: ${ci.emotion} · ${ci.score}★` : `Day ${day}: not done`} />
          );
        })}
      </div>

      {/* Verse list */}
      <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "24px" }}>
        {Array.from({ length: 21 }, (_, i) => i + 1).map(day => {
          const ci = checkinMap[day];
          const info = DAY_NAMES[day - 1];
          const done = !!ci;
          return (
            <div key={day} style={{
              display: "flex", alignItems: "center", gap: "10px",
              padding: "10px 14px", borderRadius: "10px",
              background: done ? "rgba(232,131,42,0.06)" : "transparent",
              border: `1px solid ${done ? "rgba(232,131,42,0.2)" : "var(--border)"}`,
            }}>
              <div style={{
                width: "28px", height: "28px", borderRadius: "50%", flexShrink: 0,
                background: done ? "var(--saffron)" : "var(--bg3)",
                border: done ? "none" : "1px solid var(--border)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "0.95rem", color: done ? "white" : "var(--text-dim)", fontWeight: "700",
              }}>{day}</div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "0.95rem", color: done ? "var(--text)" : "var(--text-dim)", fontFamily: "'Inter',sans-serif" }}>
                  {info?.name}
                </div>
              </div>

              {done ? (
                <>
                  <div style={{
                    padding: "3px 10px", borderRadius: "12px", flexShrink: 0,
                    background: "rgba(232,131,42,0.12)", border: "1px solid rgba(232,131,42,0.25)",
                    fontSize: "0.95rem", color: "var(--saffron)",
                  }}>
                    {ci.emotion}
                  </div>
                  <div style={{ flexShrink: 0, fontSize: "0.9rem", color: "var(--gold)", minWidth: "52px", textAlign: "right" }}>
                    {"★".repeat(ci.score || 0)}{"☆".repeat(5 - (ci.score || 0))}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", flexShrink: 0 }}>—</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Top emotions */}
      {topEmotions.length > 0 && (
        <div style={{ padding: "20px", background: "rgba(232,131,42,0.05)", border: "1px solid rgba(232,131,42,0.15)", borderRadius: "12px" }}>
          <div style={{ fontSize: "0.95rem", color: "var(--saffron)", marginBottom: "12px", fontWeight: "600" }}>
            Your Most Felt Emotions
          </div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {topEmotions.map(([emotion, count]) => (
              <div key={emotion} style={{
                padding: "6px 16px", borderRadius: "20px",
                background: "rgba(232,131,42,0.12)", border: "1px solid rgba(232,131,42,0.3)",
                fontSize: "0.95rem", color: "var(--saffron)",
                display: "flex", alignItems: "center", gap: "6px",
              }}>
                {emotion}
                <span style={{ fontSize: "0.95rem", color: "var(--gold)", fontWeight: "700" }}>×{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AlreadyDoneView({ status, graphData, onSwitchJourney, onShowCompletion, onShowGita, user, streak = 0 }) {
  // Auto-open companion tab on week milestone days so insight is visible
  const daysDone = status.checked_days?.length || 0;
  const isWeekMilestone = daysDone === 7 || daysDone === 14;
  const [tab,        setTab]        = useState(isWeekMilestone ? "companion" : "progress");
  const [reflection, setReflection] = useState("");

  useEffect(() => {
    if (daysDone < 7 || !user?.id || !status?.journey_id) return;
    fetch(`${API}/journey/reflection/${user.id}/${status.journey_id}`)
      .then(r => r.json())
      .then(d => { if (d.reflection) setReflection(d.reflection); })
      .catch(() => {});
  }, [daysDone, user?.id, status?.journey_id]);

  const tabBtn = (id, label, badge = false) => (
    <button onClick={() => setTab(id)} style={{
      padding: "8px 20px", borderRadius: "20px", cursor: "pointer",
      fontFamily: "'Inter',sans-serif", fontSize: "1rem", transition: "all 0.2s",
      background: tab === id ? "var(--saffron)" : "transparent",
      color: tab === id ? "white" : "var(--text-dim)",
      border: tab === id ? "1px solid var(--saffron)" : "1px solid var(--border)",
      position: "relative",
    }}>
      {label}
      {badge && tab !== id && (
        <span style={{
          position: "absolute", top: -4, right: -4,
          width: 10, height: 10, borderRadius: "50%",
          background: "#8b5cf6", border: "2px solid var(--bg)",
        }} />
      )}
    </button>
  );

  const daysDoneForMilestone = status.checked_days?.length || 0;

  return (
    <div className="page">
      {MILESTONE_DAYS[daysDoneForMilestone] && (
        <MilestoneBanner day={daysDoneForMilestone} />
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
        <div className="section-title" style={{ marginBottom: 0 }}>{status.icon} {status.title}</div>
        {streak > 0 && (
          <div style={{
            display: "flex", alignItems: "center", gap: "6px",
            background: "linear-gradient(135deg,rgba(255,107,43,0.15),rgba(255,69,0,0.1))",
            border: "1px solid rgba(255,107,43,0.35)",
            borderRadius: "20px", padding: "6px 14px",
          }}>
            <span style={{ fontSize: "1.2rem" }}>🔥</span>
            <div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1rem", color: "#c2410c", lineHeight: "1" }}>
                {streak} day{streak > 1 ? "s" : ""}
              </div>
              <div style={{ fontSize: "0.92rem", color: "var(--text-dim)", fontWeight: "600" }}>Streak</div>
            </div>
          </div>
        )}
      </div>
      <div className="section-sub">
        Day {status.current_day - 1} complete ✓ — {DAY_NAMES[Math.min((status.current_day - 2), 20)]?.name}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "24px", flexWrap: "wrap" }}>
        {tabBtn("progress", "Progress")}
        {tabBtn("companion", "🕯️ Companion", isWeekMilestone)}
        {tabBtn("community", "👥 Group")}
      </div>

      {/* Week milestone banner */}
      {isWeekMilestone && tab === "companion" && (
        <div style={{
          display: "flex", alignItems: "center", gap: "10px",
          padding: "12px 16px", marginBottom: "16px",
          background: "linear-gradient(135deg,rgba(99,102,241,0.12),rgba(139,92,246,0.06))",
          border: "1px solid rgba(99,102,241,0.3)", borderRadius: "12px",
          fontSize: "0.95rem", color: "#6d28d9",
        }}>
          <span style={{ fontSize: "1.2rem" }}>🌙</span>
          <span>Your Week {daysDone / 7} insight is ready — your companion has been watching.</span>
        </div>
      )}

      {tab === "companion" && status.journey_id && (
        <CompanionView user={user} journeyId={status.journey_id} currentDay={status.current_day} />
      )}

      {tab === "community" && (
        <Card>
          <SatsangGroupSection user={user} journeyType={status.journey_type} />
        </Card>
      )}

      {tab === "progress" && (<>

      <Card>
        <div style={{ textAlign: "center", padding: "12px 0" }}>
          <div style={{ fontSize: "2rem", marginBottom: "8px" }}>🙏</div>
          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.1rem", color: "var(--saffron)", marginBottom: "8px" }}>
            Today's check-in is complete
          </div>
          <div style={{ color: "var(--text-dim)" }}>
            Come back tomorrow for Day {status.current_day}
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ marginTop: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "1.05rem", color: "var(--text-dim)", marginBottom: "8px" }}>
            <span>Progress</span>
            <span>{status.checked_days?.length || 0} / 21 days</span>
          </div>
          <div style={{ background: "var(--bg)", borderRadius: "4px", height: "6px", overflow: "hidden" }}>
            <div style={{
              width: `${((status.checked_days?.length || 0) / 21) * 100}%`,
              height: "100%", background: "var(--saffron)", borderRadius: "4px",
              transition: "width 0.5s ease",
            }} />
          </div>
        </div>

        {/* Day dots — 7-column grid (3 rows of 7 = 21 days) */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "6px", marginTop: "16px" }}>
          {Array.from({ length: 21 }, (_, i) => i + 1).map(day => {
            const done = (status.checked_days || []).includes(day);
            const today = day === status.current_day;
            return (
              <div key={day} style={{
                aspectRatio: "1", borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "0.9rem", fontWeight: "600",
                background: done ? "var(--saffron)" : today ? "rgba(232,131,42,0.2)" : "var(--bg3)",
                color: done ? "white" : today ? "var(--saffron)" : "var(--text-dim)",
                border: today && !done ? "1px solid var(--saffron)" : "1px solid transparent",
              }}>
                {day}
              </div>
            );
          })}
        </div>
      </Card>

      {graphData && graphData.points && graphData.points.length >= 2 && (
        <Card>
          <TransformationGraph points={graphData.points} />
        </Card>
      )}

      {graphData && graphData.points && graphData.points.length >= 1 && (
        <Card>
          <EmotionHeatmap
            points={graphData.points}
            checkedDays={status.checked_days || []}
            currentDay={status.current_day}
          />
        </Card>
      )}

      {daysDone >= 7 && (reflection || graphData?.resonant_verse) && (
        <Card>
          {graphData?.resonant_verse && (
            <div style={{ marginBottom: reflection ? "16px" : 0 }}>
              <Label>Most Resonant Verse</Label>
              <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                <div style={{
                  background: "rgba(232,131,42,0.1)", border: "1px solid var(--border)",
                  borderRadius: "8px", padding: "10px 16px", textAlign: "center", flexShrink: 0,
                }}>
                  <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.1rem", color: "var(--saffron)" }}>
                    BG {graphData.resonant_verse.chapter}.{graphData.resonant_verse.verse}
                  </div>
                  <div style={{ fontSize: "0.9rem", color: "var(--text-dim)", marginTop: "2px" }}>
                    Day {graphData.resonant_verse.day}
                  </div>
                </div>
                <div>
                  <div style={{ color: "var(--saffron)", fontSize: "1rem", marginBottom: "4px" }}>
                    {"★".repeat(graphData.resonant_verse.score)}{"☆".repeat(5 - graphData.resonant_verse.score)}
                  </div>
                  <div style={{ fontSize: "0.9rem", color: "var(--text-dim)" }}>
                    The verse that moved you most
                  </div>
                </div>
              </div>
            </div>
          )}

          {reflection && (
            <div style={{ borderTop: graphData?.resonant_verse ? "1px solid var(--border)" : "none", paddingTop: graphData?.resonant_verse ? "16px" : 0 }}>
              <Label>Your Reflection So Far</Label>
              <div style={{
                fontFamily: "'Inter',sans-serif", fontSize: "1.15rem",
                color: "var(--text)", lineHeight: "1.7", }}>
                "{reflection}"
              </div>
            </div>
          )}
        </Card>
      )}

      </>)}

      {(status?.checked_days?.length || 0) >= 21 && onShowCompletion && (
        <div
          onClick={onShowCompletion}
          style={{
            marginTop: "16px", padding: "16px 20px", borderRadius: "12px", cursor: "pointer",
            background: "linear-gradient(135deg,rgba(201,149,58,0.18),rgba(232,131,42,0.12))",
            border: "1px solid rgba(201,149,58,0.5)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "1.6rem" }}>🏆</span>
            <div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1rem", color: "var(--gold)" }}>
                View Your Transformation Report
              </div>
              <div style={{ fontSize: "0.9rem", color: "var(--text-dim)", marginTop: "2px" }}>
                21 days complete — see your full journey
              </div>
            </div>
          </div>
          <span style={{ color: "var(--gold)", fontSize: "1.3rem" }}>›</span>
        </div>
      )}

      <div
        onClick={onSwitchJourney}
        style={{
          marginTop: "12px", padding: "14px 18px", borderRadius: "12px", cursor: "pointer",
          background: "var(--bg2)", border: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ fontSize: "1.4rem" }}>🔄</span>
          <div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.05rem", color: "var(--text)" }}>
              Change Sankalpa
            </div>
            <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginTop: "2px" }}>
              Switch to a different 21-day path
            </div>
          </div>
        </div>
        <span style={{ color: "var(--text-dim)", fontSize: "1.2rem" }}>›</span>
      </div>

      {onShowGita && (
        <div onClick={onShowGita} style={{
          marginTop: "12px", padding: "14px 20px", borderRadius: "12px", cursor: "pointer",
          background: "rgba(201,149,58,0.08)", border: "1px solid rgba(201,149,58,0.25)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "1.4rem" }}>📖</span>
            <div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "0.95rem", color: "var(--gold)" }}>
                Your Gita
              </div>
              <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginTop: "2px" }}>
                Every verse, every emotion, your 21-day arc
              </div>
            </div>
          </div>
          <span style={{ color: "var(--gold)", fontSize: "1.3rem" }}>›</span>
        </div>
      )}
    </div>
  );
}

// ── Completion Screen ─────────────────────────────────────────────────────────

function CompletionView({ status, graphData, user, onNewJourney }) {
  const avg    = graphData?.avg_score || 0;
  const pts    = graphData?.points || [];
  const first  = pts[0]?.score || 0;
  const last   = pts[pts.length - 1]?.score || 0;
  const delta  = last - first;
  const firstEmo = pts[0]?.emotion || "";
  const lastEmo  = pts[pts.length - 1]?.emotion || "";
  const resonant = graphData?.resonant_verse || null;

  const [cardUrl,      setCardUrl]      = useState(null);
  const [cardLoading,  setCardLoading]  = useState(false);
  const [reflection,   setReflection]   = useState("");
  const [storyShared,  setStoryShared]  = useState(false);
  const [storySharing, setStorySharing] = useState(false);
  const cardRef = useRef(null);

  // Fetch AI reflection on mount
  useEffect(() => {
    if (!user?.id || !status?.journey_id) return;
    fetch(`${API}/journey/reflection/${user.id}/${status.journey_id}`)
      .then(r => r.json())
      .then(d => { if (d.reflection) setReflection(d.reflection); })
      .catch(() => {});
  }, [user?.id, status?.journey_id]);

  const buildCard = useCallback(async () => {
    setCardLoading(true);
    try {
      const result = await generateJourneyCard({
        title:          status?.title || "Transformation",
        icon:           status?.icon  || "🌟",
        avg_score:      avg,
        first_score:    first,
        last_score:     last,
        first_emotion:  firstEmo,
        last_emotion:   lastEmo,
        days_done:      pts.length || 21,
        resonant_verse: resonant,
        ai_reflection:  reflection,
      });
      setCardUrl(result.url);
    } catch { /* non-critical */ }
    setCardLoading(false);
  }, [status, avg, first, last, firstEmo, lastEmo, pts.length, resonant, reflection]);

  useEffect(() => { buildCard(); }, [buildCard]);

  const shareWhatsApp = async () => {
    const emoji   = delta > 0 ? "📈" : "🙏";
    const shift   = delta > 0 ? `+${delta} points across 21 days` : "21 days of honest inquiry";
    const caption =
      `${emoji} *I completed the 21-Day ${status?.title || "Sankalpa"} Challenge on VedSanga*\n\n` +
      `21 days. The Bhagavad Gita. One verse, one practice, every single day. ${shift}.\n\n` +
      `If you are carrying something right now — this is for you.\n` +
      `vedsanga.com  •  वेदसङ्ग\n\n#BhagavadGita #VedSanga #21DayChallenge #Sankalpa #Transformation`;

    // Try native share with image on mobile
    if (cardUrl && navigator.canShare) {
      try {
        const res  = await fetch(cardUrl);
        const blob = await res.blob();
        const file = new File([blob], "vedsanga-journey.png", { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: caption });
          return;
        }
      } catch { /* fall through */ }
    }
    // Desktop fallback
    if (cardUrl) {
      const a = document.createElement("a");
      a.href = cardUrl; a.download = "vedsanga-journey.png"; a.click();
    }
    setTimeout(() => window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, "_blank"), 400);
  };

  const identityLabel = JOURNEY_IDENTITY[status?.journey_type] || "Sadhak";

  return (
    <div className="page" style={{ maxWidth: "700px" }}>
      <div style={{ textAlign: "center", padding: "32px 0 20px" }}>
        {/* Gita Wrapped — identity-first header */}
        <div style={{
          display: "inline-block", marginBottom: "20px",
          padding: "6px 20px", borderRadius: "24px",
          background: "rgba(201,149,58,0.12)", border: "1px solid rgba(201,149,58,0.4)",
          fontSize: "0.95rem", color: "var(--gold)", fontWeight: "600",
        }}>
          21-Day Sankalpa · Complete
        </div>

        <div style={{ fontSize: "3.2rem", marginBottom: "14px" }}>🏆</div>

        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.5rem", color: "var(--saffron)", marginBottom: "10px" }}>
          The {identityLabel}'s Journey
        </div>
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.2rem", color: "var(--text)", lineHeight: "1.7", maxWidth: "420px", margin: "0 auto 8px" }}>
          You came as someone carrying {status?.title?.toLowerCase() || "a struggle"}.<br />
          You leave as a {identityLabel}.
        </div>
        <div style={{ fontSize: "1rem", color: "var(--text-dim)" }}>
          21 days. One verse at a time. You are not who you were on Day 1.
        </div>
      </div>

      <EarnedPracticeCard journeyType={status?.journey_type} />

      {/* Stats */}
      <Card style={{ textAlign: "center" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "16px", marginBottom: "16px" }}>
          {[
            { label: "Days Completed", value: `${pts.length || 21}` },
            { label: "Average Score",  value: `${avg}/5` },
            { label: "Score Shift",    value: delta >= 0 ? `+${delta}` : `${delta}`,
              color: delta > 0 ? "#15803d" : delta < 0 ? "#dc2626" : "var(--saffron)" },
          ].map(s => (
            <div key={s.label} style={{
              background: "rgba(232,131,42,0.08)", borderRadius: "8px", padding: "16px",
            }}>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.4rem", color: s.color || "var(--saffron)" }}>
                {s.value}
              </div>
              <div style={{ fontSize: "1rem", color: "var(--text-dim)", marginTop: "4px" }}>{s.label}</div>
            </div>
          ))}
        </div>
        {delta > 0 && (
          <div style={{
            padding: "12px 20px", background: "rgba(74,222,128,0.08)",
            border: "1px solid rgba(74,222,128,0.2)", borderRadius: "8px",
            color: "#15803d", fontSize: "1rem", }}>
            Your inner score improved by {delta} points across 21 days. That is real.
          </div>
        )}
      </Card>

      {/* Transformation graph */}
      {pts.length >= 2 && (
        <Card>
          <TransformationGraph points={pts} />
        </Card>
      )}

      {/* Emotion heatmap */}
      {pts.length >= 1 && (
        <Card>
          <EmotionHeatmap
            points={pts}
            checkedDays={pts.map(p => p.day)}
            currentDay={22}
          />
        </Card>
      )}

      {/* Resonant verse + AI reflection */}
      {(graphData?.resonant_verse || reflection) && (
        <Card>
          {graphData?.resonant_verse && (
            <div style={{ marginBottom: reflection ? "16px" : 0 }}>
              <Label>Most Resonant Verse</Label>
              <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                <div style={{
                  background: "rgba(232,131,42,0.1)", border: "1px solid var(--border)",
                  borderRadius: "8px", padding: "10px 16px", textAlign: "center", flexShrink: 0,
                }}>
                  <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.1rem", color: "var(--saffron)" }}>
                    BG {graphData.resonant_verse.chapter}.{graphData.resonant_verse.verse}
                  </div>
                  <div style={{ fontSize: "0.9rem", color: "var(--text-dim)", marginTop: "2px" }}>
                    Day {graphData.resonant_verse.day}
                  </div>
                </div>
                <div>
                  <div style={{ color: "var(--saffron)", fontSize: "1rem", marginBottom: "4px" }}>
                    {"★".repeat(graphData.resonant_verse.score)}{"☆".repeat(5 - graphData.resonant_verse.score)}
                  </div>
                  <div style={{ fontSize: "0.9rem", color: "var(--text-dim)" }}>
                    The verse that moved you most across 21 days
                  </div>
                </div>
              </div>
            </div>
          )}
          {reflection && (
            <div style={{ borderTop: graphData?.resonant_verse ? "1px solid var(--border)" : "none", paddingTop: graphData?.resonant_verse ? "16px" : 0 }}>
              <Label>Your 21-Day Reflection</Label>
              <div style={{
                fontFamily: "'Inter',sans-serif", fontSize: "1.15rem",
                color: "var(--text)", lineHeight: "1.7", }}>
                "{reflection}"
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Share card preview */}
      <Card>
        <Label>Your Transformation Card</Label>
        <div style={{ fontSize: "1rem", color: "var(--text-dim)", marginBottom: "16px" }}>
          Download and share your journey on WhatsApp or Instagram Stories.
        </div>

        {/* Card preview */}
        <div style={{
          borderRadius: "12px", overflow: "hidden", marginBottom: "16px",
          border: "1px solid var(--border)",
          background: "var(--bg3)",
          display: "flex", alignItems: "center", justifyContent: "center",
          minHeight: "200px",
        }}>
          {cardLoading ? (
            <div style={{ color: "var(--text-dim)", padding: "40px" }}>
              Generating your card...
            </div>
          ) : cardUrl ? (
            <img
              ref={cardRef}
              src={cardUrl}
              alt="Journey completion card"
              style={{ width: "100%", height: "auto", display: "block", borderRadius: "12px" }}
            />
          ) : (
            <div style={{ color: "var(--text-dim)", padding: "40px" }}>
              Card preview unavailable
            </div>
          )}
        </div>

        {/* Share buttons */}
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {cardUrl && (
            <button onClick={() => downloadCard(cardUrl, `vedsanga-${status?.title?.replace(/\s+/g,"-").toLowerCase() || "journey"}.png`)}
              style={{
                display: "flex", alignItems: "center", gap: "6px",
                padding: "10px 18px", borderRadius: "8px",
                background: "var(--saffron)",
                border: "none", color: "white",
                fontFamily: "'Inter',sans-serif", fontSize: "1rem", fontWeight: "600",
                cursor: "pointer",
              }}>
              ⬇ Download Card
            </button>
          )}
          <button onClick={shareWhatsApp} style={{
            display: "flex", alignItems: "center", gap: "6px",
            padding: "10px 18px", borderRadius: "8px",
            background: "rgba(37,211,102,0.12)",
            border: "1px solid rgba(37,211,102,0.35)",
            color: "#25D366",
            fontFamily: "'Inter',sans-serif", fontSize: "1rem",
            cursor: "pointer",
          }}>
            📱 Share on WhatsApp
          </button>
        </div>
      </Card>

      {/* Community share */}
      <Card>
        <Label>Share with the Community</Label>
        <div style={{ fontSize: "1rem", color: "var(--text-dim)", lineHeight: "1.7", marginBottom: "16px" }}>
          Anonymously add your story to the VedSanga community feed. Others walking the same path will see what shifted for you — no name, no details, just the arc of your journey.
        </div>
        {storyShared ? (
          <div style={{
            padding: "14px 20px", borderRadius: "10px",
            background: "rgba(74,222,128,0.08)",
            border: "1px solid rgba(74,222,128,0.25)",
            color: "#15803d", fontSize: "1rem",
          }}>
            Your story is now part of the community. Someone will find strength in it.
          </div>
        ) : (
          <button
            onClick={async () => {
              if (storySharing || !user?.id || !status?.journey_id) return;
              setStorySharing(true);
              try {
                const res = await fetch(`${API}/stories/share`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ user_id: user.id, journey_id: status.journey_id }),
                });
                const d = await res.json();
                if (d.shared || d.error === "already_shared") setStoryShared(true);
              } catch {}
              setStorySharing(false);
            }}
            disabled={storySharing}
            style={{
              padding: "10px 20px", borderRadius: "8px",
              background: "rgba(167,139,250,0.12)",
              border: "1px solid rgba(167,139,250,0.35)",
              color: "#6d28d9",
              fontFamily: "'Inter',sans-serif", fontSize: "1rem",
              cursor: storySharing ? "not-allowed" : "pointer",
              opacity: storySharing ? 0.6 : 1,
            }}
          >
            {storySharing ? "Sharing..." : "🌿 Share anonymously"}
          </button>
        )}
      </Card>

      <Btn onClick={onNewJourney}>Take a new Sankalpa</Btn>
    </div>
  );
}

// ── Daily Journey Screen ──────────────────────────────────────────────────────

function DailyJourneyScreen({ user, journeyData, onCheckinDone, onOpenCompanion, streak = 0, onSwitchJourney }) {
  const [step, setStep]                   = useState("chips");   // "chips" | "verse" | "action" | "slider"
  const [selectedChip, setSelectedChip]   = useState(null);
  const [sliderValue, setSliderValue]     = useState(3);
  const [reflectionText, setReflectionText] = useState("");
  const [checkinSaved, setCheckinSaved]   = useState(null);
  const [saving, setSaving]               = useState(false);
  const [commentary, setCommentary]       = useState(null);
  const [loadingComm, setLoadingComm]     = useState(false);
  const [action, setAction]               = useState(null);
  const [actionDone, setActionDone]       = useState(false);
  const [bookmarked, setBookmarked]       = useState(false);
  const [bookmarkSaving, setBookmarkSaving] = useState(false);

  const { journey_id, journey_type, title, icon, current_day, total_days, phase, emotion_chips, verse,
          day_name, day_theme, week = (current_day <= 7 ? 1 : current_day <= 14 ? 2 : 3),
          journaling_prompt } = journeyData;

  // Check if today's verse is already bookmarked
  useEffect(() => {
    if (!verse || !user?.id) return;
    fetch(`${API}/bookmarks/${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => {
        const saved = (d.bookmarks || []).some(
          b => b.chapter === verse.chapter && b.verse === verse.verse
        );
        setBookmarked(saved);
      })
      .catch(() => {});
  }, [verse?.chapter, verse?.verse, user?.id]);

  const toggleBookmark = async () => {
    if (!verse || !user?.id || bookmarkSaving) return;
    setBookmarkSaving(true);
    try {
      if (bookmarked) {
        await fetch(`${API}/bookmark/${encodeURIComponent(user.id)}/${verse.chapter}/${verse.verse}`, { method: "DELETE" });
        setBookmarked(false);
      } else {
        await fetch(`${API}/bookmark`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: user.id, chapter: verse.chapter, verse: verse.verse }),
        });
        setBookmarked(true);
      }
    } catch { /* non-critical */ }
    setBookmarkSaving(false);
  };

  const fetchCommentary = async (emotion) => {
    if (!verse?.chapter || !verse?.verse) return;
    setLoadingComm(true);
    try {
      const r = await fetch(`${API}/shloka/commentary/${verse.chapter}/${verse.verse}?theme=${journey_type}&emotion=${encodeURIComponent(emotion || "")}`);
      const d = await r.json();
      setCommentary(d);
    } catch { /* show verse without commentary */ }
    setLoadingComm(false);
  };

  const fetchAction = async () => {
    if (!verse?.chapter || !verse?.verse) return;
    try {
      const r = await fetch(`${API}/journey/action/${journey_type}/${verse.chapter}/${verse.verse}`);
      const d = await r.json();
      if (d.action) setAction(d.action);
    } catch { /* non-critical */ }
  };

  const handleChipSelect = (chip) => {
    setSelectedChip(chip);
    setStep("verse");
    fetchCommentary(chip);
    fetchAction();
  };

  const handleSubmit = async () => {
    if (!selectedChip || !verse) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/journey/checkin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id:         user.id,
          journey_id,
          day:             current_day,
          chapter:         verse.chapter,
          verse:           verse.verse,
          emotion_chip:    selectedChip,
          score_after:     sliderValue,
          reflection_text: reflectionText.trim(),
        }),
      });
      const d = await r.json();
      if (d.saved) {
        setCheckinSaved({ completed: d.completed, next_day: d.next_day });
      }
    } catch { /* handled */ }
    setSaving(false);
  };

  // ── Completion card (shown after save) ────────────────────────────────────
  if (checkinSaved) {
    const scoreLabel = ["", "Still heavy", "A little lighter", "Steadier", "Much clearer", "At peace"][sliderValue];
    return (
      <div className="page">
        <div className="section-title">{icon} Day {current_day} — {DAY_NAMES[Math.min(current_day - 1, 20)]?.name}</div>
        <div className="section-sub">{title} Sankalpa</div>
        <Card>
          <div style={{ textAlign: "center", padding: "8px 0 20px" }}>
            <div style={{ fontSize: "2.4rem", marginBottom: "12px" }}>🙏</div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.1rem", color: "var(--saffron)", marginBottom: "6px" }}>
              Day {current_day} saved
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: "24px", marginTop: "16px" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "1rem", color: "var(--text-dim)", marginBottom: "4px" }}>You felt</div>
                <div style={{ color: "var(--cream)", fontSize: "1.05rem" }}>{selectedChip}</div>
              </div>
              <div style={{ width: "1px", background: "var(--border)" }} />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "1rem", color: "var(--text-dim)", marginBottom: "4px" }}>After reading</div>
                <div style={{ color: "var(--saffron)", fontSize: "1.05rem" }}>{"★".repeat(sliderValue)}{"☆".repeat(5 - sliderValue)} · {scoreLabel}</div>
              </div>
            </div>
          </div>

          <div style={{ borderTop: "1px solid var(--border)", paddingTop: "20px" }}>
            <div style={{ fontSize: "1rem", color: "var(--text-dim)", marginBottom: "16px", textAlign: "center" }}>
              Want to go deeper on today's verse?
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Btn onClick={() => onOpenCompanion({
                day:         current_day,
                title,
                journey_type,
                verseRef:    `BG ${verse.chapter}.${verse.verse}`,
                verseMeaning: verse.meaning,
                emotion:     selectedChip,
                score:       sliderValue,
                scoreLabel,
              })}>
                Reflect with the companion →
              </Btn>
              <Btn outline onClick={() => onCheckinDone(checkinSaved)}>
                Done for today
              </Btn>
            </div>
          </div>
        </Card>

        <DayShareCard
          day={current_day}
          dayName={DAY_NAMES[Math.min(current_day - 1, 20)]?.name}
          dayTheme={DAY_NAMES[Math.min(current_day - 1, 20)]?.theme}
          journeyTitle={title}
          journeyIcon={icon}
          journeyType={journey_type}
          daysChecked={Array.from({ length: current_day - 1 }, (_, i) => i + 1)}
        />

        <SatsangCommunity user={user} journeyType={journey_type} currentDay={current_day} />
      </div>
    );
  }

  // Score emoji
  const scoreLabel = ["", "Still heavy", "A little lighter", "Steadier", "Much clearer", "At peace"][sliderValue];

  const phaseNum = current_day <= 7 ? 1 : current_day <= 14 ? 2 : 3;
  const phaseColors = ["", "rgba(232,131,42,0.6)", "rgba(201,149,58,0.6)", "rgba(74,222,128,0.5)"];

  return (
    <div className="page">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ fontSize: "1.6rem" }}>{icon}</span>
          <div>
            <div className="section-title" style={{ marginBottom: "0" }}>{title}</div>
            <div style={{ fontSize: "1.05rem", color: "var(--text-dim)" }}>
              Phase {phaseNum} · {phase}
            </div>
          </div>
        </div>
        {streak > 0 && (
          <div style={{
            display: "flex", alignItems: "center", gap: "6px",
            background: "linear-gradient(135deg,rgba(255,107,43,0.15),rgba(255,69,0,0.1))",
            border: "1px solid rgba(255,107,43,0.35)",
            borderRadius: "20px", padding: "6px 14px", flexShrink: 0,
          }}>
            <span style={{ fontSize: "1.1rem" }}>🔥</span>
            <div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "0.95rem", color: "#c2410c", lineHeight: "1" }}>
                {streak}
              </div>
              <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", fontWeight: "600" }}>Streak</div>
            </div>
          </div>
        )}
      </div>

      {/* Progress */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "1rem", color: "var(--text-dim)", marginBottom: "6px" }}>
          <span>Day {current_day} of {total_days}</span>
          <span>{Math.round(((current_day - 1) / 21) * 100)}% complete</span>
        </div>
        <div style={{ background: "var(--bg3)", borderRadius: "4px", height: "4px" }}>
          <div style={{
            width: `${((current_day - 1) / 21) * 100}%`,
            height: "100%", borderRadius: "4px",
            background: `linear-gradient(90deg, var(--saffron), ${phaseColors[phaseNum]})`,
            transition: "width 0.5s",
          }} />
        </div>
        {/* Day dots — compact */}
        <div style={{ display: "flex", gap: "4px", marginTop: "8px", flexWrap: "wrap" }}>
          {Array.from({ length: 21 }, (_, i) => i + 1).map(day => (
            <div key={day} style={{
              width: "28px", height: "28px", borderRadius: "50%",
              fontSize: "0.95rem", display: "flex", alignItems: "center", justifyContent: "center",
              background: day < current_day ? "var(--saffron)"
                : day === current_day ? "rgba(232,131,42,0.3)" : "var(--bg3)",
              color: day < current_day ? "white" : day === current_day ? "var(--saffron)" : "var(--text-dim)",
              border: day === current_day ? "1px solid var(--saffron)" : "1px solid transparent",
              fontWeight: day === current_day ? "700" : "400",
            }}>
              {day}
            </div>
          ))}
        </div>
      </div>

      {/* ── Day Name + Mantra ── */}
      <DayNameBanner day={current_day} journeyTitle={title} />
      <MantraChip week={week} />

      {/* ── STEP 1: Emotion Chips ── */}
      {step === "chips" && (
        <Card>
          <Label>How are you feeling right now?</Label>
          <div style={{ fontSize: "1rem", color: "var(--text-dim)", marginBottom: "20px" }}>
            Tap one to receive today's verse
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            {emotion_chips.map(chip => (
              <button key={chip} onClick={() => handleChipSelect(chip)} style={{
                padding: "10px 20px", borderRadius: "24px", cursor: "pointer",
                fontFamily: "'Inter',sans-serif", fontSize: "1rem",
                background: "rgba(232,131,42,0.08)", border: "1px solid rgba(232,131,42,0.25)",
                color: "var(--text)", transition: "all 0.2s",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(232,131,42,0.22)"; e.currentTarget.style.borderColor = "var(--saffron)"; e.currentTarget.style.color = "var(--cream)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(232,131,42,0.08)"; e.currentTarget.style.borderColor = "rgba(232,131,42,0.25)"; e.currentTarget.style.color = "var(--text)"; }}
              >
                {chip}
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* ── STEP 2+: Verse Card ── */}
      {(step === "verse" || step === "slider") && verse && (
        <>
          {/* Emotion shown */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            background: "rgba(232,131,42,0.1)", border: "1px solid rgba(232,131,42,0.3)",
            borderRadius: "20px", padding: "6px 16px", marginBottom: "16px",
            fontSize: "1rem", color: "var(--saffron)",
          }}>
            You felt: <strong>{selectedChip}</strong>
            <button onClick={() => { setStep("chips"); setSelectedChip(null); setCommentary(null); }}
              style={{ background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer", fontSize: "1rem", marginLeft: "4px" }}>
              ✕
            </button>
          </div>

          {/* Identity address — Karma Yogi / Dharma Seeker etc */}
          <div style={{
            fontFamily: "'Inter',sans-serif", fontSize: "1.15rem",
            color: "var(--text-dim)", marginBottom: "16px", lineHeight: "1.6", textAlign: "center",
          }}>
            The Gita speaks to the{" "}
            <strong style={{ color: "var(--saffron)", fontStyle: "normal" }}>
              {JOURNEY_IDENTITY[journey_type] || "Sadhak"}
            </strong>{" "}
            in you today.
          </div>

          {/* Verse card */}
          <Card>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "rgba(232,131,42,0.1)", border: "1px solid rgba(232,131,42,0.3)", borderRadius: "20px", padding: "4px 12px", fontSize: "1rem", color: "var(--saffron)" }}>
                Day {current_day} · BG {verse.chapter}.{verse.verse}
              </div>
              <button
                onClick={toggleBookmark}
                disabled={bookmarkSaving}
                title={bookmarked ? "Remove bookmark" : "Save verse"}
                style={{
                  background: bookmarked ? "rgba(232,131,42,0.15)" : "transparent",
                  border: bookmarked ? "1px solid var(--saffron)" : "1px solid var(--border)",
                  borderRadius: "8px", padding: "6px 12px", cursor: "pointer",
                  color: bookmarked ? "var(--saffron)" : "var(--text-dim)",
                  fontSize: "0.9rem", transition: "all 0.2s", display: "flex", alignItems: "center", gap: "5px",
                }}
              >
                {bookmarked ? "🔖 Saved" : "🔖 Save"}
              </button>
            </div>

            {/* Sanskrit */}
            {verse.sanskrit && (
              <div style={{ fontFamily: "'Noto Sans Devanagari',serif", fontSize: "1.3rem", color: "var(--cream)", lineHeight: "1.8", marginBottom: "8px", textShadow: "0 0 20px rgba(232,131,42,0.3)" }}>
                {verse.sanskrit}
              </div>
            )}
            {verse.transliteration && (
              <div style={{ fontSize: "1rem", color: "var(--text-dim)", marginBottom: "16px", lineHeight: "1.6", fontStyle: "italic" }}>
                {verse.transliteration}
              </div>
            )}

            {/* Divider */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px", margin: "16px 0" }}>
              <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
              <div style={{ width: "8px", height: "8px", background: "var(--saffron)", transform: "rotate(45deg)" }} />
              <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
            </div>

            <div style={{ fontSize: "1.15rem", lineHeight: "1.9", color: "var(--text)", marginBottom: "20px" }}>
              {verse.meaning}
            </div>

            {/* Commentary */}
            {loadingComm && (
              <div style={{ color: "var(--text-dim)", fontSize: "1rem" }}>
                Loading commentary...
              </div>
            )}
            {commentary && !loadingComm && (
              <div style={{ borderTop: "1px solid var(--border)", paddingTop: "20px" }}>
                {commentary.story && (
                  <div style={{ marginBottom: "14px" }}>
                    <Label style={{ marginBottom: "6px" }}>The story</Label>
                    <div style={{ fontSize: "1.05rem", color: "var(--text)", lineHeight: "1.8" }}>{commentary.story}</div>
                  </div>
                )}
                {commentary.meaning && (
                  <div style={{ marginBottom: "14px" }}>
                    <Label style={{ marginBottom: "6px" }}>What it means for you</Label>
                    <div style={{ fontSize: "1.05rem", color: "var(--text)", lineHeight: "1.8" }}>{commentary.meaning}</div>
                  </div>
                )}
                {commentary.reflection && (
                  <div style={{
                    padding: "14px 18px", background: "rgba(232,131,42,0.07)",
                    border: "1px solid rgba(232,131,42,0.2)", borderRadius: "8px",
                    color: "var(--cream)", fontSize: "1rem", lineHeight: "1.7",
                  }}>
                    🕯️ {commentary.reflection}
                  </div>
                )}
              </div>
            )}

            {step === "verse" && (
              <div style={{ marginTop: "24px" }}>
                <Btn onClick={() => setStep("action")}>
                  I've read it — today's practice →
                </Btn>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ── STEP 3: Practice (week-aware) ── */}
      {step === "action" && (
        <>
          <PracticeCard
            day={current_day}
            practiceType={week === 1 ? "pranayama" : week === 2 ? "journaling" : "sadhana"}
            journalingPrompt={journaling_prompt}
            aiAction={action}
            actionDone={actionDone}
            onActionDone={setActionDone}
            onContinue={() => setStep("slider")}
          />
          {current_day === 5 && (
            <InvitationCard journeyTitle={title} journeyType={journey_type} />
          )}
        </>
      )}

      {/* ── STEP 4: Slider ── */}
      {step === "slider" && (
        <Card>
          <Label>After reading — how do you feel?</Label>
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.3rem", color: "var(--saffron)", marginBottom: "4px" }}>
              {sliderValue} / 5
            </div>
            <div style={{ color: "var(--text-dim)" }}>{scoreLabel}</div>
          </div>

          {/* Slider */}
          <input type="range" min="1" max="5" step="1" value={sliderValue}
            onChange={e => setSliderValue(Number(e.target.value))}
            style={{
              width: "100%", appearance: "none", height: "6px",
              background: `linear-gradient(90deg, var(--saffron) ${(sliderValue - 1) * 25}%, var(--bg3) ${(sliderValue - 1) * 25}%)`,
              borderRadius: "4px", outline: "none", cursor: "pointer", marginBottom: "12px",
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "1rem", color: "var(--text-dim)", marginBottom: "24px" }}>
            <span>Still heavy</span>
            <span>At peace</span>
          </div>

          {/* Optional reflection */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ fontSize: "0.9rem", color: "var(--text-dim)", marginBottom: "8px" }}>
              What does this bring up for you? <span style={{ opacity: 0.6 }}>(optional)</span>
            </div>
            <textarea
              value={reflectionText}
              onChange={e => setReflectionText(e.target.value)}
              placeholder="A thought, a memory, something that shifted..."
              rows={3}
              style={{
                width: "100%", background: "var(--bg3)", border: "1px solid var(--border)",
                borderRadius: "8px", padding: "12px 14px", color: "var(--text)",
                fontFamily: "'Inter',sans-serif", fontSize: "1rem", lineHeight: "1.6",
                resize: "vertical", outline: "none", boxSizing: "border-box",
              }}
              onFocus={e => { e.target.style.borderColor = "rgba(232,131,42,0.5)"; }}
              onBlur={e => { e.target.style.borderColor = "var(--border)"; }}
            />
          </div>

          <Btn onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : `Save Day ${current_day} ✓`}
          </Btn>
        </Card>
      )}

      {onSwitchJourney && (
        <div
          onClick={onSwitchJourney}
          style={{
            marginTop: "12px", padding: "14px 18px", borderRadius: "12px", cursor: "pointer",
            background: "var(--bg2)", border: "1px solid var(--border)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "1.4rem" }}>🔄</span>
            <div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.05rem", color: "var(--text)" }}>
                Change Sankalpa
              </div>
              <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginTop: "2px" }}>
                Switch to a different 21-day path
              </div>
            </div>
          </div>
          <span style={{ color: "var(--text-dim)", fontSize: "1.2rem" }}>›</span>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN JourneyPage
// ══════════════════════════════════════════════════════════════════════════════

export default function JourneyPage({ user, onOpenCompanion, onStreakUpdate }) {
  const [status,      setStatus]      = useState(null);
  const [graphData,   setGraphData]   = useState(null);
  const [view,        setView]        = useState("loading"); // "loading" | "select" | "daily" | "done" | "complete"
  const [todayData,   setTodayData]   = useState(null);
  const [graceBanner, setGraceBanner] = useState(false);
  const [graceLoading, setGraceLoading] = useState(false);

  const loadStatus = async () => {
    try {
      const r = await fetch(`${API}/journey/status/${user.id}`);
      const d = await r.json();
      setStatus(d);

      if (!d.active) {
        setView("select");
        return;
      }

      // Load graph
      if (d.journey_id && (d.checked_days?.length || 0) >= 2) {
        const gr = await fetch(`${API}/journey/graph/${user.id}/${d.journey_id}`);
        const gd = await gr.json();
        setGraphData(gd);
      }

      // Journey complete?
      if (d.completed_at) {
        setView("complete");
        return;
      }

      // Load today's data
      const tr = await fetch(`${API}/journey/today/${user.id}`);
      const td = await tr.json();

      if (td.error) {
        setView("select");
        return;
      }

      setTodayData(td);

      if (td.already_done) {
        setView("done");
      } else {
        setView("daily");
        if (d.grace_available) setGraceBanner(true);
      }
    } catch {
      setView("select");
    }
  };

  useEffect(() => { loadStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStart = async (journeyType, force = false) => {
    try {
      // If user has an active journey and picks the SAME type — just resume, no reset
      if (status?.journey_type === journeyType && !force) {
        setView("loading");
        await loadStatus();
        return;
      }

      // If switching to a DIFFERENT journey, ask for confirmation first
      if (status?.journey_type && status.journey_type !== journeyType && !force) {
        const day = status.current_day || 1;
        const confirmed = window.confirm(
          `You are on Day ${day} of your current journey.\n\nSwitching will permanently erase your progress.\n\nAre you sure you want to start a new journey?`
        );
        if (!confirmed) {
          setView("daily");
          return;
        }
      }

      await fetch(`${API}/journey/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id, journey_type: journeyType, force: true }),
      });
      setView("loading");
      await loadStatus();
    } catch { /* show select */ }
  };

  const handleCheckinDone = async ({ completed }) => {
    if (onStreakUpdate) onStreakUpdate();
    setView("loading");
    await loadStatus();
  };

  const handleUseGrace = async () => {
    if (!status?.journey_id || graceLoading) return;
    setGraceLoading(true);
    try {
      await fetch(`${API}/journey/use-grace`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id, journey_id: status.journey_id }),
      });
      setGraceBanner(false);
      if (onStreakUpdate) onStreakUpdate();
      setView("loading");
      await loadStatus();
    } catch { /* non-critical */ }
    setGraceLoading(false);
  };

  if (view === "loading") {
    return (
      <div className="page">
        <div style={{ color: "var(--text-dim)", padding: "60px", textAlign: "center" }}>
          🕉️ Loading your sankalpa...
        </div>
      </div>
    );
  }

  if (view === "select") {
    return <JourneySelection onStart={handleStart} />;
  }

  if (view === "complete") {
    return <CompletionView status={status} graphData={graphData} user={user} onNewJourney={() => setView("select")} />;
  }

  if (view === "gita" && status) {
    return (
      <>
        <div style={{ padding: "8px 16px" }}>
          <button onClick={() => setView("done")} style={{
            background: "none", border: "none", color: "var(--text-dim)",
            cursor: "pointer", fontFamily: "'Inter',sans-serif", fontSize: "1rem",
            display: "flex", alignItems: "center", gap: "6px", padding: "0",
          }}>← Back</button>
        </div>
        <YourGitaView status={status} graphData={graphData} user={user} />
      </>
    );
  }

  if (view === "done" && status) {
    return <AlreadyDoneView user={user} status={status} graphData={graphData}
      onSwitchJourney={() => setView("select")}
      onShowCompletion={() => setView("complete")}
      onShowGita={() => setView("gita")}
      streak={status.streak || 0} />;
  }

  if (view === "daily" && todayData) {
    return (
      <>
        {graceBanner && (
          <div style={{
            margin: "16px 32px 0",
            padding: "16px 20px",
            borderRadius: "12px",
            background: "linear-gradient(135deg,rgba(232,131,42,0.12),rgba(201,149,58,0.08))",
            border: "1px solid rgba(232,131,42,0.4)",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px",
            flexWrap: "wrap",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span style={{ fontSize: "1.6rem" }}>🕯️</span>
              <div>
                <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "0.95rem", color: "var(--saffron)", marginBottom: "2px" }}>
                  Life happened
                </div>
                <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
                  You missed yesterday. Use your one grace day to keep your streak alive.
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={handleUseGrace} disabled={graceLoading} style={{
                padding: "8px 18px", borderRadius: "8px", cursor: graceLoading ? "default" : "pointer",
                background: "rgba(232,131,42,0.18)", border: "1px solid var(--saffron)",
                color: "var(--saffron)", fontFamily: "'Inter',sans-serif", fontSize: "1rem",
                opacity: graceLoading ? 0.6 : 1,
              }}>
                {graceLoading ? "Saving..." : "Use Grace Day"}
              </button>
              <button onClick={() => setGraceBanner(false)} style={{
                padding: "8px 14px", borderRadius: "8px", cursor: "pointer",
                background: "transparent", border: "1px solid var(--border)",
                color: "var(--text-dim)", fontFamily: "'Inter',sans-serif", fontSize: "1rem",
              }}>
                Dismiss
              </button>
            </div>
          </div>
        )}
        <DailyJourneyScreen
          user={user}
          journeyData={todayData}
          onCheckinDone={handleCheckinDone}
          onOpenCompanion={onOpenCompanion}
          streak={status?.streak || 0}
          onSwitchJourney={() => setView("select")}
        />
      </>
    );
  }

  return null;
}
